import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Game = ({ isPreviewMode = false, hasPaidPlan = false }) => {
  // Removed excessive console.log to prevent spam during re-renders
  
  // User & Auth
  const [userId] = useState('demo_user'); // TODO: Get from actual auth context
  
  // Check if user is admin
  // TODO: Replace with actual admin check from your auth system
  // For now, showing zoom controls to everyone for testing
  // In production, check: user.email.endsWith('@admin.com') or user.role === 'admin'
  const [isAdmin] = useState(() => {
    // TEMPORARY: Always true for testing
    // PRODUCTION: Uncomment line below and remove 'return true'
    // const userRole = localStorage.getItem('userRole');
    // return userRole === 'admin';
    return true; // Everyone sees zoom controls for now
  });
  
  // Game State
  // App-shell caches: gameConfig is admin-managed (characters, taxi settings,
  // location coords) — heavy and stable, big win to cache. gameProgress is
  // per-user state (gems, position) — also worth caching for instant render.
  const _readGcCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedGameConfig = _readGcCache('gameConfigCache');
  const _cachedGameProgress = _readGcCache('gameProgressCache');

  const [gameProgress, setGameProgress] = useState(_cachedGameProgress);
  const [gameConfig, setGameConfig] = useState(_cachedGameConfig);
  const [loading, setLoading] = useState(true);
  
  // UI State
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('locations');
  
  // Canvas State
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1); // Start at 100% zoom for clear visibility
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [draggingNodeIndex, setDraggingNodeIndex] = useState(null); // For dragging road nodes
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [characterImages, setCharacterImages] = useState({});
  const [taxiImage, setTaxiImage] = useState(null);
  const [userAvatarImage, setUserAvatarImage] = useState(null);
  const [userMaleAvatarImage, setUserMaleAvatarImage] = useState(null);
  const [userFemaleAvatarImage, setUserFemaleAvatarImage] = useState(null);
  const [selectedGender, setSelectedGender] = useState('male'); // Gender selection for player avatar
  const [previewData, setPreviewData] = useState(null); // Data from admin editor in preview mode
  
  // Game Mechanics
  const [timeOfDay, setTimeOfDay] = useState('day');
  const [cycleProgress, setCycleProgress] = useState(0);
  const [energy, setEnergy] = useState(100);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showDialogue, setShowDialogue] = useState(false);
  
  // Gem Features
  const [dailyLoginClaimed, setDailyLoginClaimed] = useState(false);
  const [rankInfo, setRankInfo] = useState({ rank: 'Beginner', nextRank: 500 });
  const [spendingLocked, setSpendingLocked] = useState(false);
  const [savingsGoal, setSavingsGoal] = useState({ target: 1000, current: 0 });
  const [smartSuggestion, setSmartSuggestion] = useState(null);
  
  // Game Visibility & Access Control
  const [gameIsVisible, setGameIsVisible] = useState(true);
  const [gameIsPro, setGameIsPro] = useState(false);
  const [userHasMembership, setUserHasMembership] = useState(hasPaidPlan);
  useEffect(() => { setUserHasMembership(hasPaidPlan); }, [hasPaidPlan]);

  // Transactions
  const [recentTransactions, setRecentTransactions] = useState([]);
  
  // Show/Hide markers toggle (admin feature)
  const [showMarkers, setShowMarkers] = useState(true);
  
  // Taxi bargaining state
  const [showTaxiBargaining, setShowTaxiBargaining] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [taxiPrice, setTaxiPrice] = useState(0);
  const [isTaxiAnimating, setIsTaxiAnimating] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null); // Track where taxi is now
  const [previousLocation, setPreviousLocation] = useState(null); // Track where user came from
  const [showTaxiConversation, setShowTaxiConversation] = useState(false); // Modal for "Speak to Taxi"
  
  // Travel system (simplified - using smooth continuous animation)
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  
  // NEW: Smooth continuous taxi movement (like reference game)
  const [taxiPath, setTaxiPath] = useState([]); // Array of {x, y} points
  const [taxiPathIndex, setTaxiPathIndex] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const taxiPositionRef = useRef({ x: 0, y: 0 });
  const taxiPathRef = useRef([]);
  const taxiPathIndexRef = useRef(0);
  const taxiRotationRef = useRef(0);
  
  // Road Network System
  const [roads, setRoads] = useState([]);
  const [roadNodes, setRoadNodes] = useState([]); // All road nodes from API (perimeter + interior)
  const [selectedPath, setSelectedPath] = useState(null);
  const [charactersOnPath, setCharactersOnPath] = useState([]);
  const [showRoadCharacterModal, setShowRoadCharacterModal] = useState(false);
  const [currentRoadCharacter, setCurrentRoadCharacter] = useState(null);
  
  // Bargaining system - Realistic Moroccan style
  const [showBargainModal, setShowBargainModal] = useState(false);
  const [bargainStep, setBargainStep] = useState(1); // 1 = first offer, 2 = counter offer
  const [taxiInitialPrice, setTaxiInitialPrice] = useState(100);
  const [userFirstOffer, setUserFirstOffer] = useState(null);
  const [availablePrices, setAvailablePrices] = useState([]);
  const [hasBargained, setHasBargained] = useState(false);
  const [finalPrice, setFinalPrice] = useState(0);
  
  // Location info modal
  const [showLocationInfoModal, setShowLocationInfoModal] = useState(false);
  const [infoLocation, setInfoLocation] = useState(null); // Track which location's info to show
  const [clickedButtonType, setClickedButtonType] = useState(null); // 'speak', 'info', 'taxi'
  
  // In-Game Guide/Assistant
  const [showGuide, setShowGuide] = useState(false);
  const [guideChoice, setGuideChoice] = useState(null); // Track which choice user selected
  
  // Conversation System
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [conversationCharacter, setConversationCharacter] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);

  // Hotel/Restaurant System
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationModalTab, setLocationModalTab] = useState('info'); // 'info', 'rooms', 'book', 'speak', 'items'
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showRoomConfirm, setShowRoomConfirm] = useState(false);
  const [playerNotes, setPlayerNotes] = useState([]);
  const [noteText, setNoteText] = useState('');


  // Pickup System - Location Based
  const [collectedItems, setCollectedItems] = useState([]);
  const [pickupItems, setPickupItems] = useState([]);
  const [itemSpawns, setItemSpawns] = useState([]); // Keep for backward compat
  const [showMerchantModal, setShowMerchantModal] = useState(false);
  const [merchantPrices, setMerchantPrices] = useState([]);
  const [selectedItemsToSell, setSelectedItemsToSell] = useState([]);
  const [showItemPickupModal, setShowItemPickupModal] = useState(false);
  const [locationItems, setLocationItems] = useState([]); // Items available at current location
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeOffers, setTradeOffers] = useState([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryItems, setDeliveryItems] = useState([]);
  
  // Darija phrase system
  const [currentPhrase, setCurrentPhrase] = useState(null);
  const [phraseLoading, setPhraseLoading] = useState(false);

  // Load random Darija phrase
  const loadRandomPhrase = async (destination = null) => {
    try {
      setPhraseLoading(true);
      const destParam = destination ? `&destination=${encodeURIComponent(destination)}` : '';
      const response = await axios.get(`${API_URL}/api/game/random-phrase/${userId}?${destParam}`);
      setCurrentPhrase(response.data.phrase);
    } catch (error) {
      console.error('Failed to load phrase:', error);
    } finally {
      setPhraseLoading(false);
    }
  };


  // Handle location click for taxi travel
  const handleLocationClick = async (location, buttonType = 'taxi') => {
    
    // Handle INFO button - show location-specific modal (hotel, restaurant, etc.)
    if (buttonType === 'info') {
      console.log('🏨 Location clicked:', location.name, 'Type:', location.location_type);
      
      const locationType = location.location_type || 'landmark';
      const locationName = (location.name || '').toLowerCase();
      
      // Check if it's a hotel or restaurant by type OR by name
      const isHotel = locationType === 'hotel' || locationName.includes('hotel');
      const isRestaurant = locationType === 'restaurant' || locationName.includes('restaurant');
      
      // For hotels and restaurants, show comprehensive modal
      if (isHotel || isRestaurant) {
        console.log('✅ Opening new hotel/restaurant modal');
        
        // If hotel/restaurant doesn't have data, add sample data
        let locationWithData = {...location};
        
        if (isHotel && !location.hotel_data) {
          console.log('🏨 Adding sample hotel data');
          locationWithData.location_type = 'hotel';
          locationWithData.hotel_data = {
            rooms: [
              {id: "room_standard", type: "Standard Room", price: 500, available: 5, description: "Comfortable room with city view"},
              {id: "room_deluxe", type: "Deluxe Room", price: 800, available: 3, description: "Spacious room with balcony"},
              {id: "room_suite", type: "Royal Suite", price: 1200, available: 1, description: "Luxury suite with ocean view"}
            ],
            amenities: ["WiFi", "Pool", "Gym", "Restaurant", "Spa", "Room Service"],
            receptionist_character_id: null
          };
        }
        
        if (isRestaurant && !location.restaurant_data) {
          console.log('🍽️ Adding sample restaurant data');
          locationWithData.location_type = 'restaurant';
          locationWithData.restaurant_data = {
            menu: [
              {id: "food_tagine", name: "Tagine", price: 150, energy_bonus: 10, available: true},
              {id: "food_couscous", name: "Couscous", price: 120, energy_bonus: 10, available: true},
              {id: "food_pastilla", name: "Pastilla", price: 180, energy_bonus: 15, available: true}
            ],
            waiter_character_id: null
          };
        }
        
        setSelectedLocation(locationWithData);
        setLocationModalTab('info');
        setShowLocationModal(true);
        
        // Load player notes
        try {
          const notesRes = await axios.get(`${API_URL}/api/game/notes/${userId}`);
          setPlayerNotes(notesRes.data.notes || []);
        } catch (err) {
          console.error('Error loading notes:', err);
        }
        return;
      }
      
      // For other locations, show old modal
      console.log('⚠️ Showing old modal for:', locationName);
      setInfoLocation(location);
      setShowLocationInfoModal(true);
      return;
    }
    
    // Handle SPEAK button - show conversation modal
    if (buttonType === 'speak') {
      setShowTaxiConversation(true);
      return;
    }
    
    // Handle START button - Set starting location
    if (buttonType === 'start') {
      setCurrentLocation(location);
      console.log(`🎯 Starting location set to: ${location.name}`);
      
      // Save starting location
      try {
        await axios.post(`${API_URL}/api/game/save-location`, null, {
          params: { user_id: userId, location_id: location.id }
        });
      } catch (error) {
        console.error('Failed to save starting location:', error);
      }
      
      // Update taxi position to this location
      const destX = location.marker_x || location.position?.x || 500;
      const destY = location.marker_y || location.position?.y || 500;
      setGameConfig(prev => ({
        ...prev,
        taxi_settings: {
          ...prev.taxi_settings,
          position: { x: destX + 50, y: destY + 30, z: 0 }
        }
      }));
      return;
    }
    
    // Handle TAXI button
    if (buttonType === 'taxi') {
      // Load a random Darija phrase for the taxi driver
      loadRandomPhrase(location.name);
      
      // If no current location set, set it and open taxi modal
      if (!currentLocation) {
        setCurrentLocation(location);
        setSelectedDestination(location); // Set for modal display
        console.log(`🚕 Starting location set to: ${location.name}`);
        setShowTaxiConversation(true); // Open taxi modal
        return;
      }
      
      // If clicking same location (shouldn't happen with new UI, but just in case)
      if (currentLocation.id === location.id) {
        setShowTaxiConversation(true);
        return;
      }
      
      // Different location - proceed with travel
    }
    
    // Set the selected destination for taxi modal
    setSelectedDestination(location);
    
    // Open taxi modal - user will bargain and then travel
    setShowTaxiConversation(true);
  };

  // REMOVED: Old stop-by-stop continueToNextStop() function
  // Now using smooth continuous animation loop (System B) in useEffect below

  // Check if any character is near a position (used when taxi stops)
  const findNearbyCharacter = (position) => {
    if (!gameConfig?.characters || !position) return null;
    
    const PROXIMITY_THRESHOLD = 100; // pixels
    
    for (const character of gameConfig.characters) {
      const dx = character.position.x - position.x;
      const dy = character.position.y - position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < PROXIMITY_THRESHOLD) {
        return character;
      }
    }
    return null;
  };
  
  // Open conversation with character
  const startConversation = async (character) => {
    console.log('🎯 startConversation called with character:', character);
    
    try {
      // First try to get conversation flow from new system
      console.log('📞 Fetching conversation flow...');
      let flow = null;
      
      try {
        // Try location-specific flow first
        const flowResponse = await axios.get(
          `${API_URL}/api/conversation-flows/character/${character.id}/location/${currentLocation?.id || 'default'}`
        );
        flow = flowResponse.data;
        console.log('✅ Found location-specific flow:', flow.name);
      } catch (e) {
        // Try character's default flow
        try {
          const flowsResponse = await axios.get(`${API_URL}/api/conversation-flows/character/${character.id}`);
          if (flowsResponse.data && flowsResponse.data.length > 0) {
            flow = flowsResponse.data[0];
            console.log('✅ Found default flow:', flow.name);
          }
        } catch (e2) {
          console.log('No flow found, checking old system...');
        }
      }
      
      if (flow) {
        // Use new flow-based conversation system
        console.log('🎭 Using new flow system with', flow.nodes.length, 'nodes');
        
        // Convert flow to conversation format
        const startNode = flow.nodes.find(n => n.id === flow.startNodeId);
        const firstContentNode = startNode?.nextNodeId 
          ? flow.nodes.find(n => n.id === startNode.nextNodeId)
          : flow.nodes.find(n => n.type === 'message');
        
        if (firstContentNode) {
          const convertedNode = convertFlowNodeToConversationNode(firstContentNode, flow.nodes);
          
          setActiveConversation({
            id: flow.id,
            title: flow.name,
            nodes: flow.nodes,
            start_node_id: firstContentNode.id,
            isFlowBased: true
          });
          setConversationCharacter(character);
          setCurrentNode(convertedNode);
          setShowConversationModal(true);
          return;
        }
      }
      
      // Fallback to old conversation system
      console.log('📞 Fetching from old conversation API...');
      const response = await axios.get(`${API_URL}/api/morocco-conversations/conversations`);
      const allConversations = response.data;
      
      const conversation = allConversations.find(conv => conv.character_id === character.id);
      
      if (conversation) {
        console.log('✅ Found old conversation:', conversation.title);
        
        setActiveConversation(conversation);
        setConversationCharacter(character);
        
        const startNode = conversation.nodes.find(n => n.id === conversation.start_node_id);
        setCurrentNode(startNode);
        setShowConversationModal(true);
      } else {
        console.log('❌ No conversation found for character:', character.name);
        // Show nice modal instead of alert
        setActiveConversation({
          title: `${character.name}`,
          nodes: [{
            id: 'no_conversation',
            character_message: `Hi! I'm ${character.name}. My conversation isn't ready yet, but you can create one in the Admin Panel → Conversations tab!`,
            character_emotion: 'neutral',
            choices: [{
              id: 'ok',
              text: 'Okay, got it!',
              next_node_id: null
            }],
            gives_gems: 0
          }],
          start_node_id: 'no_conversation'
        });
        setConversationCharacter(character);
        setCurrentNode({
          id: 'no_conversation',
          character_message: `Hi! I'm ${character.name}. My conversation isn't ready yet, but you can create one in the Admin Panel → Conversations tab!`,
          character_emotion: 'neutral',
          choices: [{
            id: 'ok',
            text: 'Okay, got it!',
            next_node_id: null
          }],
          gives_gems: 0
        });
        setShowConversationModal(true);
      }
    } catch (error) {
      console.error('❌ Failed to load conversation:', error);
      // Show error in modal
      setActiveConversation({
        title: 'Error',
        nodes: [{
          id: 'error',
          character_message: `Failed to load conversation: ${error.message}. Please try again later.`,
          character_emotion: 'sad',
          choices: [{
            id: 'ok',
            text: 'Close',
            next_node_id: null
          }],
          gives_gems: 0
        }],
        start_node_id: 'error'
      });
      setConversationCharacter(character);
      setCurrentNode({
        id: 'error',
        character_message: `Failed to load conversation: ${error.message}. Please try again later.`,
        character_emotion: 'sad',
        choices: [{
          id: 'ok',
          text: 'Close',
          next_node_id: null
        }],
        gives_gems: 0
      });
      setShowConversationModal(true);
    }
  };
  
  // Convert flow node to old conversation node format for rendering
  const convertFlowNodeToConversationNode = (flowNode, allNodes) => {
    if (!flowNode) return null;
    
    const node = {
      id: flowNode.id,
      type: flowNode.type,
      character_message: flowNode.data?.text || '',
      character_emotion: flowNode.data?.emotion || 'neutral',
      darija_text: flowNode.data?.darija || '',
      translation: flowNode.data?.translation || '',
      gives_gems: 0,
      choices: []
    };
    
    // Handle different node types
    if (flowNode.type === 'message') {
      node.character_message = flowNode.data?.text || '';
      // Create a "Continue" choice if there's a next node
      if (flowNode.nextNodeId) {
        node.choices = [{
          id: 'continue',
          text: '→ Continue',
          next_node_id: flowNode.nextNodeId
        }];
      } else {
        node.choices = [{
          id: 'end',
          text: '→ End Conversation',
          next_node_id: null
        }];
      }
    } else if (flowNode.type === 'choice') {
      node.character_message = flowNode.data?.text || 'Choose an option:';
      node.choices = (flowNode.data?.options || []).map(opt => ({
        id: opt.id,
        text: opt.text,
        next_node_id: opt.nextNodeId
      }));
    } else if (flowNode.type === 'action') {
      node.character_message = flowNode.data?.message || 'Action performed!';
      
      // Apply action
      if (flowNode.data?.actionType === 'give_gems') {
        node.gives_gems = flowNode.data?.amount || 0;
        node.character_message = flowNode.data?.message || `You received ${flowNode.data?.amount || 0} gems!`;
      }
      
      if (flowNode.nextNodeId) {
        node.choices = [{
          id: 'continue',
          text: '→ Continue',
          next_node_id: flowNode.nextNodeId
        }];
      } else {
        node.choices = [{
          id: 'end',
          text: '→ End Conversation',
          next_node_id: null
        }];
      }
    } else if (flowNode.type === 'end') {
      node.character_message = flowNode.data?.text || 'Goodbye!';
      node.choices = [{
        id: 'end',
        text: '→ End Conversation',
        next_node_id: null
      }];
    }
    
    return node;
  };
  
  // Handle conversation choice selection
  const handleConversationChoice = (choice) => {
    console.log('User selected choice:', choice.text);
    
    // Add to conversation history
    setConversationHistory(prev => [
      ...prev,
      { role: 'user', content: choice.text }
    ]);
    
    // Give gems if the current node has gems
    if (currentNode.gives_gems && currentNode.gives_gems > 0) {
      setGameProgress(prev => ({
        ...prev,
        gems_balance: (prev.gems_balance || 0) + currentNode.gives_gems
      }));
      console.log(`💎 Earned ${currentNode.gives_gems} gems!`);
    }
    
    // Trigger quest if specified
    if (currentNode.triggers_quest) {
      console.log(`📜 Quest triggered: ${currentNode.triggers_quest}`);
      // TODO: Implement quest system
    }
    
    // Navigate to next node
    if (choice.next_node_id) {
      // Check if this is a flow-based conversation
      if (activeConversation?.isFlowBased) {
        // Find the flow node and convert it
        const nextFlowNode = activeConversation.nodes.find(n => n.id === choice.next_node_id);
        if (nextFlowNode) {
          const convertedNode = convertFlowNodeToConversationNode(nextFlowNode, activeConversation.nodes);
          setCurrentNode(convertedNode);
        } else {
          console.error('Next flow node not found:', choice.next_node_id);
          closeConversation();
        }
      } else {
        // Old conversation system
        const nextNode = activeConversation.nodes.find(n => n.id === choice.next_node_id);
        if (nextNode) {
          setCurrentNode(nextNode);
        } else {
          console.error('Next node not found:', choice.next_node_id);
          closeConversation();
        }
      }
    } else {
      // End conversation
      closeConversation();
    }
  };
  
  // Close conversation modal
  const closeConversation = () => {
    setShowConversationModal(false);
    setActiveConversation(null);
    setCurrentNode(null);
    setConversationCharacter(null);
    setConversationHistory([]);
    // REMOVED: setShowStopModal(true) - no longer using stop modal
  };


  // Book hotel room
  const bookHotelRoom = async (room) => {
    const currentGems = gameProgress?.gems_balance || 0;
    
    if (currentGems < room.price) {
      alert('❌ Not enough gems! You need ' + room.price + ' gems.');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/game/book-room`, null, {
        params: {
          user_id: userId,
          location_id: selectedLocation.id,
          room_id: room.id
        }
      });
      
      if (response.data.success) {
        // Update local game progress
        setGameProgress(prev => ({
          ...prev,
          gems_balance: response.data.new_balance,
          current_room: response.data.current_room,
          energy: 100
        }));
        
        // Add transaction
        setRecentTransactions(prev => [
          {
            item: `Booked ${room.type} room at ${selectedLocation.name}`,
            amount: -room.price,
            timestamp: new Date()
          },
          ...prev
        ]);
        
        // Show room confirmation with room number
        setSelectedRoom({...room, room_number: response.data.room_number});
        setShowRoomConfirm(true);
        setShowLocationModal(false);
      }
    } catch (err) {
      console.error('Error booking room:', err);
      alert(err.response?.data?.detail || 'Failed to book room');
    }
  };
  
  // Add note about location
  const addLocationNote = async () => {
    if (!noteText.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/game/add-note`, null, {
        params: {
          user_id: userId,
          location_id: selectedLocation.id,
          location_name: selectedLocation.name,
          note_text: noteText
        }
      });
      
      // Reload notes
      const notesRes = await axios.get(`${API_URL}/api/game/notes/${userId}`);
      setPlayerNotes(notesRes.data.notes || []);
      setNoteText('');
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };


  // Load pickup system data
  useEffect(() => {
    const loadPickupData = async () => {
      try {
        // Load item definitions
        const itemsRes = await axios.get(`${API_URL}/api/game/items`);
        setPickupItems(itemsRes.data.items || []);
        
        // Load spawns
        const spawnsRes = await axios.get(`${API_URL}/api/game/item-spawns`);
        setItemSpawns(spawnsRes.data.spawns || []);
        
        // Load player inventory
        if (userId) {
          const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
          setCollectedItems(progressRes.data.collected_items || []);
        }
      } catch (error) {
        console.error('Error loading pickup data:', error);
      }
    };
    
    loadPickupData();
  }, [userId]);

  // Calculate dynamic taxi price based on road distance (2-20 gems)
  // Uses the new 48-node road network pathfinding
  const calculateDynamicTaxiPrice = async (fromLocId, toLocId) => {
    try {
      // Use new find-path-locations endpoint (48-node road network)
      const pathResponse = await axios.get(`${API_URL}/api/game/find-path-locations/${fromLocId}/${toLocId}?dense=false`);
      const pathData = pathResponse.data;
      
      if (!pathData.success || !pathData.path || pathData.path.length === 0) {
        return { basePrice: 5, path: [] }; // Default price if no path
      }
      
      // Use the suggested price from backend (based on distance)
      const calculatedPrice = pathData.suggested_price || 5;
      
      console.log(`💰 Dynamic price: ${calculatedPrice} gems (${pathData.node_count} road nodes, ${pathData.total_distance}px)`);
      
      return { 
        basePrice: calculatedPrice, 
        path: pathData.path,
        nodeCount: pathData.node_count,
        distance: pathData.total_distance
      };
    } catch (error) {
      console.error('Error calculating taxi price:', error);
      return { basePrice: 5, path: [] }; // Default on error
    }
  };

  // Confirm taxi travel and start SMOOTH CONTINUOUS animation
  // Uses the 48-node road network for pathfinding
  const confirmTaxiTravel = async () => {
    const currentGems = gameProgress?.gems_balance || 0;
    
    if (currentGems < taxiPrice) {
      alert('❌ Not enough gems! You need ' + taxiPrice + ' gems.');
      return;
    }
    
    setShowTaxiBargaining(false);
    setShowTaxiConversation(false); // Close taxi modal
    setIsTaxiAnimating(true);
    setIsMoving(true);
    
    console.log('🚕 Starting taxi travel (48-NODE ROAD SYSTEM)...');
    console.log('   From:', currentLocation?.name, 'ID:', currentLocation?.id);
    console.log('   To:', selectedDestination?.name, 'ID:', selectedDestination?.id);
    
    // Use NEW find-path-locations endpoint that uses 48-node road network
    try {
      const toId = selectedDestination?.id || selectedDestination;
      
      // Get TAXI's CURRENT position (not location marker!)
      // This allows taxi to start from wherever it currently is
      const taxiCurrentPos = gameConfig?.taxi_settings?.position || taxiPositionRef.current;
      const startX = Math.round(taxiCurrentPos?.x || 0);
      const startY = Math.round(taxiCurrentPos?.y || 0);
      
      // Get destination location coordinates
      const destLocation = gameConfig?.locations?.find(l => l.id === toId);
      if (!destLocation) {
        alert('Destination not found!');
        setIsTaxiAnimating(false);
        setIsMoving(false);
        return;
      }
      
      const endX = destLocation.marker_x || destLocation.position?.x || 0;
      const endY = destLocation.marker_y || destLocation.position?.y || 0;
      
      console.log('🚕 Starting taxi travel from CURRENT POSITION...');
      console.log('   From taxi at: (' + startX + ', ' + startY + ')');
      console.log('   To:', selectedDestination?.name, 'at (' + endX + ', ' + endY + ')');
      
      // Call endpoint with COORDINATES (taxi position → destination)
      const pathResponse = await axios.get(`${API_URL}/api/game/find-path/${startX}/${startY}/${endX}/${endY}?dense=true`);
      const pathData = pathResponse.data;
      
      console.log('📍 Path found:', pathData.message);
      console.log('🛣️ Via road nodes:', pathData.start_node, '→', pathData.end_node);
      console.log('📌 Total waypoints:', pathData.total_waypoints);
      
      if (!pathData.success || !pathData.path || pathData.path.length === 0) {
        console.error('❌ No path found between locations');
        alert('No road connects these locations!');
        setIsTaxiAnimating(false);
        setIsMoving(false);
        return;
      }
      
      // Get the dense path (50px intervals for smooth movement)
      const path = pathData.path;
      console.log('✅ Using smooth path with', path.length, 'waypoints');
      
      // Initialize taxi position at FIRST ROAD NODE (taxi stays on road!)
      const startPos = path[0];
      taxiPositionRef.current = { x: startPos.x, y: startPos.y };
      taxiPathRef.current = path;
      taxiPathIndexRef.current = 0;
      
      // IMMEDIATELY move taxi to the first road node
      setGameConfig(prev => ({
        ...prev,
        taxi_settings: {
          ...prev.taxi_settings,
          position: { x: startPos.x, y: startPos.y, z: 0 }
        }
      }));
      
      // Center camera on taxi
      const canvas = document.querySelector('canvas');
      if (canvas) {
        const currentZoom = zoom || 1;
        setPan({ 
          x: (canvas.width / 2) - (startPos.x * currentZoom), 
          y: (canvas.height / 2) - (startPos.y * currentZoom) 
        });
      }
      
      // Set the path for the animation loop
      setTaxiPath(path);
      setTaxiPathIndex(0);
      
      // Deduct gems upfront for the journey
      const newBalance = currentGems - taxiPrice;
      setGameProgress(prev => ({
        ...prev,
        gems_balance: newBalance
      }));
      
      // Add transaction
      setRecentTransactions(prev => [
        { item: `Taxi to ${selectedDestination.name}`, amount: -taxiPrice, timestamp: new Date() },
        ...prev
      ]);
      
      console.log('🚗 Taxi animation started! Following', path.length, 'waypoints...');
      
    } catch (error) {
      console.error('Path finding error:', error);
      setIsTaxiAnimating(false);
      setIsMoving(false);
      alert('Failed to find path: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Initialize Game
  useEffect(() => {
    // Load initial data from API (even in preview mode)
    initializeGame();
    
    // Poll for PREVIEW updates every 500ms for instant cross-browser sync
    // DISABLED in preview mode - using BroadcastChannel instead
    if (isAdmin && !isPreviewMode) {
      const previewInterval = setInterval(async () => {
        try {
          const previewRes = await axios.get(`${API_URL}/api/game/preview`);
          const previewData = previewRes.data;
          
          // If preview data exists, use it (temporary editor changes)
          // Otherwise, keep current saved config
          if (previewData && (previewData.taxi_settings || previewData.characters || previewData.markers || previewData.locations)) {
            // Update gameConfig with preview data
            setGameConfig(prev => ({
              ...prev,
              taxi_settings: previewData.taxi_settings || prev?.taxi_settings,
              characters: previewData.characters || prev?.characters,
              markers: previewData.markers || prev?.markers,
              locations: previewData.locations || prev?.locations
            }));
          }
        } catch (error) {
          // Ignore errors, just skip this poll
        }
      }, 500); // Poll every 500ms for smooth updates
      
      return () => {
        clearInterval(previewInterval);
      };
    }
  }, []);

  // Listen for preview updates from admin editor (preview mode only)
  useEffect(() => {
    if (!isPreviewMode) return;

    const broadcastChannel = new BroadcastChannel('game-editor-preview');
    
    broadcastChannel.onmessage = (event) => {
      if (event.data.type === 'UPDATE_PREVIEW') {
        console.log('📡 Preview update received from editor');
        console.log('  - Characters:', event.data.data.characters?.length || 0);
        console.log('  - Locations:', event.data.data.locations?.length || 0);
        console.log('  - Taxi position:', event.data.data.taxiSettings?.position);
        
        setPreviewData(event.data.data);
        
        // Update game config with preview data
        setGameConfig(prevConfig => {
          const newConfig = {
            ...prevConfig,
            ...event.data.data
          };
          console.log(`[${Date.now()}] ✅ Game config updated, new taxi pos:`, newConfig.taxiSettings?.position);
          console.log(`[${Date.now()}] Previous taxi pos was:`, prevConfig.taxi_settings?.position);
          return newConfig;
        });
      }
    };

    return () => {
      broadcastChannel.close();
    };
  }, [isPreviewMode]);

  // ============================================
  // SMOOTH TAXI MOVEMENT - Continuous Animation Loop
  // Based on reference game implementation
  // ============================================
  
  // Keep refs in sync with state
  useEffect(() => {
    taxiPositionRef.current = gameConfig?.taxi_settings?.position || { x: 0, y: 0 };
  }, [gameConfig?.taxi_settings?.position]);
  
  useEffect(() => {
    taxiPathRef.current = taxiPath;
  }, [taxiPath]);
  
  useEffect(() => {
    taxiPathIndexRef.current = taxiPathIndex;
  }, [taxiPathIndex]);
  
  useEffect(() => {
    taxiRotationRef.current = gameConfig?.taxi_settings?.rotation || 0;
  }, [gameConfig?.taxi_settings?.rotation]);
  
  // Main taxi movement animation loop (60fps using requestAnimationFrame)
  useEffect(() => {
    let animationId;
    
    const updateTaxi = () => {
      const path = taxiPathRef.current;
      const pathIndex = taxiPathIndexRef.current;
      
      // If no path or reached end, just keep looping
      if (!path || path.length === 0 || pathIndex >= path.length) {
        animationId = requestAnimationFrame(updateTaxi);
        return;
      }
      
      const current = taxiPositionRef.current;
      const target = path[pathIndex];
      
      const dx = target.x - current.x;
      const dy = target.y - current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Reached waypoint (within 3 pixels)
      if (distance < 3) {
        if (pathIndex + 1 >= path.length) {
          // Journey complete!
          console.log('🏁 Taxi journey complete!');
          
          // TAXI STAYS ON ROAD: Use the last waypoint position (already on road node)
          // target = last point from A* pathfinding = on road node
          const taxiFinalX = target.x;
          const taxiFinalY = target.y;
          
          console.log(`🚕 Taxi stopped at road node (${taxiFinalX}, ${taxiFinalY})`);
          
          // Update ref immediately
          taxiPositionRef.current = { x: taxiFinalX, y: taxiFinalY };
          
          setGameConfig(prev => ({
            ...prev,
            taxi_settings: {
              ...prev.taxi_settings,
              position: { x: taxiFinalX, y: taxiFinalY, z: 0 }
            }
          }));
          setTaxiPath([]);
          setTaxiPathIndex(0);
          setIsMoving(false);
          setIsTaxiAnimating(false);
          
          // Trigger arrival
          setPreviousLocation(currentLocation);
          setCurrentLocation(selectedDestination);
          setShowArrivalModal(true);
          
          // CRITICAL: Save location AND taxi position to backend
          const userId = localStorage.getItem('morocco_user_id');
          if (userId && selectedDestination) {
            axios.post(`${API_URL}/api/game/save-location`, null, {
              params: { 
                user_id: userId, 
                location_id: selectedDestination.id,
                taxi_position: JSON.stringify({ x: taxiFinalX, y: taxiFinalY, z: 0 })
              }
            }).then(() => {
              console.log('✅ Location saved to backend:', selectedDestination.name);
            }).catch(err => {
              console.error('Failed to save location:', err);
            });
          }
        } else {
          // Move to next waypoint
          setTaxiPathIndex(pathIndex + 1);
        }
      } else {
        // Calculate rotation to face movement direction
        const angle = Math.atan2(dx, -dy); // Note: -dy because canvas Y is inverted
        const newRotation = (angle * 180 / Math.PI + 360) % 360;
        
        // Speed: faster when far, slower when close (between 2-5 pixels per frame)
        const speed = Math.min(5, Math.max(2, distance * 0.1));
        const newX = current.x + (dx / distance) * speed;
        const newY = current.y + (dy / distance) * speed;
        
        // UPDATE REF IMMEDIATELY (critical for smooth animation!)
        taxiPositionRef.current = { x: newX, y: newY };
        taxiRotationRef.current = newRotation;
        
        // Update taxi position and rotation in state (for canvas rendering)
        setGameConfig(prev => ({
          ...prev,
          taxi_settings: {
            ...prev.taxi_settings,
            position: { x: Math.round(newX), y: Math.round(newY), z: 0 },
            rotation: newRotation
          }
        }));
        
        // Camera follows taxi
        const canvas = document.querySelector('canvas');
        if (canvas) {
          const currentZoom = zoom;
          const centerX = (canvas.width / 2) - (newX * currentZoom);
          const centerY = (canvas.height / 2) - (newY * currentZoom);
          setPan({ x: centerX, y: centerY });
        }
      }
      
      animationId = requestAnimationFrame(updateTaxi);
    };
    
    animationId = requestAnimationFrame(updateTaxi);
    return () => cancelAnimationFrame(animationId);
  }, [zoom, currentLocation, selectedDestination]);

  const initializeGame = async () => {
    try {
      // Load game config
      const configRes = await axios.get(`${API_URL}/api/game/config`);
      setGameConfig(configRes.data);
      try { localStorage.setItem('gameConfigCache', JSON.stringify(configRes.data)); } catch {}
      
      // Check game visibility and pro status
      const isVisible = configRes.data.is_visible !== false; // Default to true
      const isPro = configRes.data.is_pro === true;
      setGameIsVisible(isVisible);
      setGameIsPro(isPro);
      
      // If game is not visible, stop loading
      if (!isVisible) {
        setLoading(false);
        return;
      }
      
      // Load roads for pathfinding
      try {
        const roadsRes = await axios.get(`${API_URL}/api/game/roads`);
        setRoads(roadsRes.data.roads || []);
        console.log('🛣️ Loaded roads:', roadsRes.data.roads?.length || 0);
      } catch (error) {
        console.log('No roads configured yet');
      }
      
      // Load ALL road nodes (perimeter + interior grid)
      let loadedRoadNodes = [];
      try {
        const nodesRes = await axios.get(`${API_URL}/api/game/all-road-nodes`);
        loadedRoadNodes = nodesRes.data.nodes || [];
        setRoadNodes(loadedRoadNodes);
        console.log('📍 Loaded road nodes:', nodesRes.data.total || 0, `(${nodesRes.data.source})`);
      } catch (error) {
        console.log('No road nodes configured yet');
      }
      
      // Helper function: Find nearest road node to a position
      const findNearestRoadNode = (x, y, nodes) => {
        if (!nodes || nodes.length === 0) return null;
        let nearest = null;
        let minDist = Infinity;
        for (const node of nodes) {
          const dist = Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2);
          if (dist < minDist) {
            minDist = dist;
            nearest = node;
          }
        }
        return nearest;
      };
      
      // Helper function: Find location nearest to a road node
      const findLocationNearNode = (node, locations) => {
        if (!node || !locations) return null;
        let nearest = null;
        let minDist = Infinity;
        for (const loc of locations) {
          const locX = loc.marker_x || loc.position?.x || 0;
          const locY = loc.marker_y || loc.position?.y || 0;
          const dist = Math.sqrt((node.x - locX) ** 2 + (node.y - locY) ** 2);
          if (dist < minDist) {
            minDist = dist;
            nearest = loc;
          }
        }
        return nearest;
      };
      
      // STEP 1: Get current taxi position from config
      const configTaxiPos = configRes.data.taxi_settings?.position;
      const taxiX = configTaxiPos?.x || 0;
      const taxiY = configTaxiPos?.y || 0;
      
      // STEP 2: SNAP taxi to nearest road node (taxi must always be on road!)
      const nearestNode = findNearestRoadNode(taxiX, taxiY, loadedRoadNodes);
      let snappedTaxiPos = { x: taxiX, y: taxiY, z: 0 };
      
      if (nearestNode) {
        snappedTaxiPos = { x: nearestNode.x, y: nearestNode.y, z: 0 };
        console.log(`🚕 Snapped taxi to road node #${nearestNode.id} at (${nearestNode.x}, ${nearestNode.y})`);
        
        // Update taxi position in state
        setGameConfig(prev => ({
          ...prev,
          taxi_settings: {
            ...prev.taxi_settings,
            position: snappedTaxiPos
          }
        }));
        taxiPositionRef.current = snappedTaxiPos;
      }
      
      // STEP 3: Determine current location based on snapped taxi position
      // Find which location is closest to the taxi's road node
      const detectedLocation = findLocationNearNode(nearestNode || { x: taxiX, y: taxiY }, configRes.data.locations);
      
      if (detectedLocation) {
        console.log(`📍 Current location detected: ${detectedLocation.name}`);
        setCurrentLocation(detectedLocation);
      } else {
        console.log('📍 No location detected near taxi - user must select starting point');
      }
      
      // Helper to fix Dropbox URLs (defined earlier in function)
      const fixDropboxUrl = (url) => {
        if (!url) return url;
        if (url.includes('dropbox.com')) {
          return url.replace('dl=0', 'dl=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
        }
        return url;
      };

      // Load background
      if (configRes.data.background_url && configRes.data.background_url !== 'https://example.com/background.jpg') {
        const img = new Image();
        img.onload = () => {
          setBackgroundImage(img);
          console.log('✅ Background loaded:', configRes.data.background_url);
        };
        img.onerror = () => {
          console.error('❌ Failed to load background:', configRes.data.background_url);
          // Still set a fallback - draw without background
          setBackgroundImage(null);
        };
        // Apply Dropbox fix and crossOrigin
        img.crossOrigin = "anonymous";
        img.src = fixDropboxUrl(configRes.data.background_url);
      } else {
        console.warn('⚠️ No valid background URL, canvas will have solid color');
        setBackgroundImage(null); // Allow canvas to render without background
      }

      // Preload character images (fixDropboxUrl defined above)
      if (configRes.data.characters) {
        const images = {};
        configRes.data.characters.forEach(char => {
          if (char.image_url && char.image_url.trim() !== '') {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              images[char.id] = img;
              setCharacterImages({...images});
            };
            img.onerror = () => console.error('Failed to load character image:', char.name);
            img.src = fixDropboxUrl(char.image_url);
          }
        });
      }

      // Preload taxi image
      if (configRes.data.taxi_settings?.image_url) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => setTaxiImage(img);
        img.onerror = () => console.error('Failed to load taxi image');
        img.src = fixDropboxUrl(configRes.data.taxi_settings.image_url);
      }

      // Preload male avatar image
      if (configRes.data.user_male_avatar_settings?.image_url) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => setUserMaleAvatarImage(img);
        img.onerror = () => console.error('Failed to load male avatar image');
        img.src = fixDropboxUrl(configRes.data.user_male_avatar_settings.image_url);
      }

      // Preload female avatar image
      if (configRes.data.user_female_avatar_settings?.image_url) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => setUserFemaleAvatarImage(img);
        img.onerror = () => console.error('Failed to load female avatar image');
        img.src = fixDropboxUrl(configRes.data.user_female_avatar_settings.image_url);
      }

      // Try to load existing progress
      try {
        const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
        setGameProgress(progressRes.data);
        try { localStorage.setItem('gameProgressCache', JSON.stringify(progressRes.data)); } catch {}
        setEnergy(progressRes.data.energy);
      } catch (err) {
        // Start new game
        const startRes = await axios.post(`${API_URL}/api/game/start?user_id=${userId}`);
        setGameProgress(startRes.data.progress);
        try { localStorage.setItem('gameProgressCache', JSON.stringify(startRes.data.progress)); } catch {}
        setEnergy(startRes.data.progress.energy);
      }

      // Load transactions
      loadTransactions();
      
      setLoading(false);
    } catch (error) {
      console.error('Error initializing game:', error);
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/game/transactions/${userId}`);
      setRecentTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  // Day/Night Cycle
  useEffect(() => {
    if (!gameConfig) return;
    
    const dayDuration = (gameConfig.day_night_settings?.dayDuration || 30) * 60 * 1000;
    const nightDuration = (gameConfig.day_night_settings?.nightDuration || 10) * 60 * 1000;
    const totalCycle = dayDuration + nightDuration;

    const interval = setInterval(() => {
      const now = Date.now();
      const cyclePosition = now % totalCycle;
      
      if (cyclePosition < dayDuration) {
        setTimeOfDay('day');
        setCycleProgress((cyclePosition / dayDuration) * 100);
        // Energy drains during day
        if (energy > 0) setEnergy(prev => Math.max(0, prev - 0.1));
      } else {
        setTimeOfDay('night');
        setCycleProgress(((cyclePosition - dayDuration) / nightDuration) * 100);
        // Energy restores at night
        if (energy < 100) setEnergy(prev => Math.min(100, prev + 0.2));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameConfig, energy]);

  // Canvas Drawing
  useEffect(() => {
    if (!canvasRef.current) return; // Allow drawing even without background
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    console.log(`[${Date.now()}] 🎨 Canvas useEffect running, taxi pos:`, gameConfig?.taxi_settings?.position);
    
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Save context for background only
      ctx.save();
      
      // Apply zoom and pan ONLY to background
      ctx.translate(pan.x, pan.y);
      ctx.scale(zoom, zoom);
      
      // Draw infinite tiled background
      if (backgroundImage) {
        const tileWidth = canvas.width;
        const tileHeight = canvas.height;
        
        // Calculate which tiles are visible based on pan/zoom
        const startCol = Math.floor(-pan.x / (tileWidth * zoom)) - 1;
        const endCol = Math.ceil((canvas.width - pan.x) / (tileWidth * zoom)) + 1;
        const startRow = Math.floor(-pan.y / (tileHeight * zoom)) - 1;
        const endRow = Math.ceil((canvas.height - pan.y) / (tileHeight * zoom)) + 1;
        
        // Draw visible tiles
        for (let row = startRow; row < endRow; row++) {
          for (let col = startCol; col < endCol; col++) {
            const x = col * tileWidth;
            const y = row * tileHeight;
            ctx.drawImage(backgroundImage, x, y, tileWidth, tileHeight);
          }
        }
      } else {
        // Fallback: Draw solid color background
        const isDay = timeOfDay === 'day';
        ctx.fillStyle = isDay ? '#87CEEB' : '#2C3E50'; // Sky blue or dark blue
        ctx.fillRect(-canvas.width, -canvas.height, canvas.width * 3, canvas.height * 3);
        
        // Draw grid to show it's working
        ctx.strokeStyle = isDay ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)';
        ctx.lineWidth = 1;
        for (let i = 0; i < canvas.width * 3; i += 100) {
          ctx.beginPath();
          ctx.moveTo(i - canvas.width, -canvas.height);
          ctx.lineTo(i - canvas.width, canvas.height * 2);
          ctx.stroke();
        }
        for (let i = 0; i < canvas.height * 3; i += 100) {
          ctx.beginPath();
          ctx.moveTo(-canvas.width, i - canvas.height);
          ctx.lineTo(canvas.width * 2, i - canvas.height);
          ctx.stroke();
        }
      }
      
      ctx.restore(); // Restore before drawing characters
      
      // UNIFIED COORDINATE SYSTEM: Apply SAME transformation to ALL game elements
      // This keeps taxi on roads and everything aligned at all zoom levels
      ctx.save();
      ctx.translate(pan.x, pan.y);
      ctx.scale(zoom, zoom); // Apply zoom to game elements too!
      
      // Draw characters (preview data already merged in gameConfig via polling)
      if (gameConfig?.characters) {
        gameConfig.characters.forEach(char => {
          const x = char.position.x;
          const y = char.position.y;
          const size = char.size || 80;
          const rotation = char.rotation || 0;
          
          // Highlight if being edited (check localStorage for selected element)
          const selectedCharId = localStorage.getItem('selectedCharacterId');
          const isSelected = selectedCharId === char.id;
          
          // Draw selection circle
          if (isSelected && isAdmin) {
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.arc(x, y, size/2 + 10, 0, Math.PI * 2);
            ctx.stroke();
            
            // Draw crosshair at center
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(x - 15, y);
            ctx.lineTo(x + 15, y);
            ctx.moveTo(x, y - 15);
            ctx.lineTo(x, y + 15);
            ctx.stroke();
          }
          
          // Apply rotation transformation
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate((rotation * Math.PI) / 180); // Convert degrees to radians
          
          // Check if character has preloaded custom image
          const customImg = characterImages[char.id];
          if (customImg) {
            // Draw custom image (centered, rotated)
            ctx.drawImage(customImg, -size/2, -size/2, size, size);
          } else {
            // Draw character emoji (fallback, rotated)
            ctx.font = `${size}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(char.emoji, 0, 0);
          }
          
          ctx.restore();
          
          // Draw name label (not rotated)
          ctx.font = 'bold 14px Arial';
          ctx.fillStyle = 'white';
          ctx.strokeStyle = 'black';
          ctx.lineWidth = 3;
          ctx.strokeText(char.name, x, y - size/2 - 5);
          ctx.fillText(char.name, x, y - size/2 - 5);
          
          // Draw coordinates and rotation if selected
          if (isSelected && isAdmin) {
            ctx.font = '12px Arial';
            ctx.fillStyle = '#FFD700';
            ctx.fillText(`(${x}, ${y}) ∠${rotation}°`, x, y + size/2 + 20);
          }
        });
      }
      
      // Draw hidden gems (if enabled)
      if (gameConfig?.hidden_gems) {
        gameConfig.hidden_gems.forEach(gem => {
          if (!gem.enabled) return; // Skip disabled gems
          
          const gemEmoji = gem.type === 'ruby' ? '❤️' : 
                          gem.type === 'emerald' ? '💚' : '💎';
          const size = gem.size || 40; // Size from admin
          
          ctx.font = `${size}px Arial`;
          ctx.textAlign = 'center';
          ctx.fillText(gemEmoji, gem.x, gem.y);
        });
      }
      
      // Draw taxi (always visible) - preview data already merged in gameConfig via polling
      if (gameConfig?.taxi_settings && gameConfig.taxi_settings.position) {
        const taxi = gameConfig.taxi_settings;
        const x = taxi.position?.x || 500;
        const y = taxi.position?.y || 400;
        const size = taxi.size || 60;
        const rotation = taxi.rotation || 0;
        
        // Check if taxi is selected in editor
        const isTaxiBeingEdited = localStorage.getItem('editingTaxi') === 'true';
        
        // Draw selection circle for taxi if being edited
        if (isTaxiBeingEdited && isAdmin) {
          ctx.strokeStyle = '#FFD700';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(x, y, size/2 + 10, 0, Math.PI * 2);
          ctx.stroke();
          
          // Draw crosshair at center
          ctx.strokeStyle = '#FFD700';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x - 15, y);
          ctx.lineTo(x + 15, y);
          ctx.moveTo(x, y - 15);
          ctx.lineTo(x, y + 15);
          ctx.stroke();
        }
        
        // Apply rotation transformation for taxi body
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((rotation * Math.PI) / 180); // Convert degrees to radians
        
        // Draw custom taxi image or emoji
        if (taxiImage) {
          ctx.drawImage(taxiImage, -size/2, -size/2, size, size);
        } else {
          ctx.font = `${size}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('🚕', 0, 0);
        }
        
        // Draw FRONT indicator (green arrow pointing forward)
        ctx.beginPath();
        ctx.moveTo(0, -size/2 - 5);  // Front tip
        ctx.lineTo(-8, -size/2 + 10);
        ctx.lineTo(8, -size/2 + 10);
        ctx.closePath();
        ctx.fillStyle = '#4CAF50';  // Green for front
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw BACK indicator (red square at back)
        ctx.fillStyle = '#f44336';  // Red for back
        ctx.fillRect(-6, size/2 - 8, 12, 8);
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1;
        ctx.strokeRect(-6, size/2 - 8, 12, 8);
        
        ctx.restore();
        
        // ===== DRAW CENTER DOT LAST (on top of everything) =====
        // This dot marks the EXACT taxi position and should ALWAYS be on the road
        // Make it highly visible with large size and strong contrast
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);  // 10px radius for high visibility
        ctx.fillStyle = '#00FFFF';  // Bright cyan
        ctx.fill();
        ctx.strokeStyle = '#000000';  // Black border
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Add white inner ring for extra visibility
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, Math.PI * 2);
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw a + crosshair through center dot for precise alignment
        // Extend beyond the taxi for clear visibility
        ctx.beginPath();
        ctx.strokeStyle = '#FF00FF';  // Magenta for visibility
        ctx.lineWidth = 2;
        ctx.moveTo(x - 25, y);  // Extended horizontal line
        ctx.lineTo(x + 25, y);
        ctx.moveTo(x, y - 25);  // Extended vertical line
        ctx.lineTo(x, y + 25);
        ctx.stroke();
        
        // Draw "TAXI" label (not rotated) - positioned above taxi
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = '#FFD700';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.textAlign = 'center';
        ctx.strokeText('TAXI', x, y - size/2 - 15);
        ctx.fillText('TAXI', x, y - size/2 - 15);
        
        // Draw coordinates and rotation if being edited
        if (isTaxiBeingEdited && isAdmin) {
          ctx.font = '12px Arial';
          ctx.fillStyle = '#FFD700';
          ctx.fillText(`(${x}, ${y}) ∠${rotation}°`, x, y + size/2 + 30);
        }
      }
      
      // ============================================
      // DRAW ROAD NODES (manual placements) - Draggable + shape
      // ============================================
      if (gameConfig?.road_nodes && gameConfig.road_nodes.length > 0) {
        const nodes = gameConfig.road_nodes;
        
        // Draw connecting lines first (under the nodes)
        if (nodes.length > 1) {
          ctx.beginPath();
          ctx.strokeStyle = 'rgba(33, 150, 243, 0.3)';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          
          ctx.moveTo(nodes[0].x, nodes[0].y);
          for (let i = 1; i < nodes.length; i++) {
            ctx.lineTo(nodes[i].x, nodes[i].y);
          }
          ctx.stroke();
          ctx.setLineDash([]);
        }
        
        // Draw each node as a small + crosshair
        nodes.forEach((node, idx) => {
          const x = node.x;
          const y = node.y;
          const size = 8; // Small sharp crosshair
          
          // Draw + shape
          ctx.beginPath();
          ctx.strokeStyle = '#f44336'; // Red for visibility
          ctx.lineWidth = 2;
          
          // Vertical line of +
          ctx.moveTo(x, y - size);
          ctx.lineTo(x, y + size);
          
          // Horizontal line of +
          ctx.moveTo(x - size, y);
          ctx.lineTo(x + size, y);
          
          ctx.stroke();
          
          // Center dot
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fillStyle = '#f44336';
          ctx.fill();
          
          // Draw node number on the side (small)
          ctx.font = '9px Arial';
          ctx.fillStyle = '#333';
          ctx.textAlign = 'left';
          ctx.fillText(`${idx + 1}`, x + size + 3, y + 3);
        });
      }
      
      // Show placing mode indicator
      if (gameConfig?.placingNodes) {
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = '#4CAF50';
        ctx.textAlign = 'left';
        ctx.fillText('🔵 CLICK TO PLACE NODES', 20, 30);
      }
      
      // ============================================
      // REMOVED: Old road_grid intersection drawing
      // Now using NEW road_nodes system (156 nodes)
      // ============================================
      
      // Draw ALL ROAD NODES from API (perimeter + interior grid)
      // These are the nodes the taxi follows
      if (roadNodes && roadNodes.length > 0) {
        // Draw road node markers
        roadNodes.forEach((node, idx) => {
          // Perimeter nodes (1-48) in blue, interior nodes (49+) in green
          const isPerimeter = node.id <= 48;
          
          ctx.beginPath();
          ctx.arc(node.x, node.y, isPerimeter ? 15 : 10, 0, Math.PI * 2);
          ctx.fillStyle = isPerimeter ? 'rgba(33, 150, 243, 0.7)' : 'rgba(76, 175, 80, 0.6)'; // Blue for perimeter, green for interior
          ctx.fill();
          ctx.strokeStyle = isPerimeter ? '#1565C0' : '#2E7D32';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw node number (only for perimeter nodes to reduce clutter)
          if (isPerimeter) {
            ctx.font = 'bold 10px Arial';
            ctx.fillStyle = 'white';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${node.id}`, node.x, node.y);
          }
        });
        
        // Draw lines connecting perimeter road nodes only (to show the outer road path)
        const perimeterNodes = roadNodes.filter(n => n.id <= 48).sort((a, b) => a.id - b.id);
        if (perimeterNodes.length > 0) {
          ctx.beginPath();
          ctx.strokeStyle = 'rgba(33, 150, 243, 0.4)';
          ctx.lineWidth = 3;
          ctx.setLineDash([10, 5]);
          
          for (let i = 0; i < perimeterNodes.length; i++) {
            const node = perimeterNodes[i];
            const nextNode = perimeterNodes[(i + 1) % perimeterNodes.length]; // Loop back to first
            
            if (i === 0) {
              ctx.moveTo(node.x, node.y);
            }
            ctx.lineTo(nextNode.x, nextNode.y);
          }
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
      
      // REMOVED: Old routeMarkers drawing code (was using dead System A)
      // Now using smooth continuous animation (System B) with taxiPath

      // Draw user avatar ("You") - DYNAMIC POSITION BASED ON CURRENT LOCATION
      // Avatar is HIDDEN when taxi is animating (player is inside taxi)
      // Avatar appears at current location when not traveling
      if (!isTaxiAnimating) {
        const avatarSettings = selectedGender === 'male' 
          ? gameConfig?.user_male_avatar_settings 
          : gameConfig?.user_female_avatar_settings;
        
        // Get avatar position - use current location if available
        let avatarX, avatarY;
        
        if (currentLocation && gameConfig?.locations) {
          // Find current location and position avatar there
          const locId = typeof currentLocation === 'string' ? currentLocation : currentLocation.id;
          const currentLoc = gameConfig.locations.find(l => 
            l.id === locId || l.name?.toLowerCase() === locId?.toLowerCase()
          );
          
          if (currentLoc) {
            // Position avatar near the location marker (slightly offset)
            avatarX = (currentLoc.marker_x || currentLoc.position?.x || 500) + 50;
            avatarY = (currentLoc.marker_y || currentLoc.position?.y || 500) + 30;
          } else {
            // Fallback to avatar settings position
            avatarX = avatarSettings?.position?.x || 500;
            avatarY = avatarSettings?.position?.y || 500;
          }
        } else {
          // No current location - use default position
          avatarX = avatarSettings?.position?.x || 500;
          avatarY = avatarSettings?.position?.y || 500;
        }
        
        const size = avatarSettings?.size || 80;
        const rotation = avatarSettings?.rotation || 0;
        
        // Apply rotation
        ctx.save();
        ctx.translate(avatarX, avatarY);
        ctx.rotate((rotation * Math.PI) / 180);
        
        // Draw custom avatar image or emoji
        const avatarImg = selectedGender === 'male' ? userMaleAvatarImage : userFemaleAvatarImage;
        if (avatarImg) {
          ctx.drawImage(avatarImg, -size/2, -size/2, size, size);
        } else {
          ctx.font = `${size}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(avatarSettings?.emoji || '🧑', 0, 0);
        }
        
        ctx.restore();
        
        // Draw "YOU" label with location name
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.strokeText('YOU', avatarX, avatarY - size/2 - 5);
        ctx.fillText('YOU', avatarX, avatarY - size/2 - 5);
      } else {
        // When taxi is animating, show small avatar icon on taxi
        const taxiPos = gameConfig?.taxi_settings?.position;
        if (taxiPos) {
          ctx.font = '20px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('👤', taxiPos.x + 15, taxiPos.y - 20);
        }
      }
      
      // Draw location markers/bubbles
      if (gameConfig?.locations) {
        gameConfig.locations.forEach(loc => {
          const x = loc.marker_x || loc.position?.x || 0;
          const y = loc.marker_y || loc.position?.y || 0;
          
          if (x === 0 && y === 0) return; // Skip if no position set
          
          const text = loc.bubble_text || loc.name || 'Location';
          
          // Measure text for bubble size
          ctx.font = 'bold 16px Arial';
          const textWidth = ctx.measureText(text).width;
          const padding = 15;
          const bubbleWidth = textWidth + padding * 2;
          const bubbleHeight = 35;
          
          // Draw bubble background (speech bubble style)
          ctx.fillStyle = 'rgba(92, 107, 192, 0.95)';
          ctx.beginPath();
          ctx.roundRect(x - bubbleWidth/2, y - bubbleHeight - 10, bubbleWidth, bubbleHeight, 8);
          ctx.fill();
          
          // Draw white border
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw location pin icon
          ctx.fillStyle = '#FFD700';
          ctx.font = '24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('📍', x, y + 5);
          
          // Draw location text
          ctx.fillStyle = 'white';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(text, x, y - bubbleHeight/2 - 5);
          
          // Highlight if being edited
          const selectedLocationId = localStorage.getItem('selectedLocationId');
          if (selectedLocationId === loc.id && isAdmin) {
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(x, y, 35, 0, Math.PI * 2);
            ctx.stroke();
          }
        });
      }
      
      // Draw pickup items (fruits, etc.) - NO TAXI PROXIMITY HIGHLIGHT
      if (itemSpawns && pickupItems) {
        itemSpawns.forEach(spawn => {
          const item = pickupItems.find(i => i.id === spawn.item_id);
          if (!item) return;
          
          const x = spawn.position?.x || 0;
          const y = spawn.position?.y || 0;
          if (x === 0 && y === 0) return;
          
          // Draw sparkle effect (subtle animation)
          ctx.save();
          const time = Date.now() / 1000;
          const sparkleAlpha = 0.2 + Math.sin(time * 2) * 0.15;
          ctx.globalAlpha = sparkleAlpha;
          ctx.fillStyle = '#FFD700';
          ctx.beginPath();
          ctx.arc(x, y, 18, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          
          // Draw item emoji
          ctx.font = '28px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(item.emoji, x, y);
        });
      }
      
      ctx.restore();
    };
    
    draw();
  }, [backgroundImage, gameConfig, zoom, pan, timeOfDay, characterImages, taxiImage, userMaleAvatarImage, userFemaleAvatarImage, selectedGender, isAdmin, itemSpawns, pickupItems, isTaxiAnimating, currentLocation, roadNodes]);

  // Canvas Interaction
  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left - pan.x) / zoom;
    const clickY = (e.clientY - rect.top - pan.y) / zoom;
    
    // Check if clicking on a road node to drag it
    if (gameConfig?.road_nodes && gameConfig.road_nodes.length > 0) {
      for (let i = 0; i < gameConfig.road_nodes.length; i++) {
        const node = gameConfig.road_nodes[i];
        const dist = Math.sqrt(Math.pow(node.x - clickX, 2) + Math.pow(node.y - clickY, 2));
        if (dist < 20) { // Click within 20px of node
          setDraggingNodeIndex(i);
          console.log(`📍 Started dragging node #${i + 1}`);
          return; // Don't do other click actions
        }
      }
    }
    
    // If in node placing mode (from admin), send coordinates to parent
    if (gameConfig?.placingNodes && window.opener) {
      const nodeData = {
        type: 'ROAD_NODE_PLACED',
        x: Math.round(clickX),
        y: Math.round(clickY)
      };
      window.opener.postMessage(nodeData, '*');
      console.log('📍 Node placed at:', nodeData.x, nodeData.y);
      return; // Don't do other click actions
    }
    
    // Check if clicked on an item spawn
    let clickedSpawn = null;
    if (itemSpawns && pickupItems) {
      for (const spawn of itemSpawns) {
        const spawnX = spawn.position?.x || 0;
        const spawnY = spawn.position?.y || 0;
        if (spawnX === 0 && spawnY === 0) continue;
        
        const distance = Math.sqrt(Math.pow(clickX - spawnX, 2) + Math.pow(clickY - spawnY, 2));
        if (distance < 25) {
          clickedSpawn = spawn;
          break;
        }
      }
    }
    
    // If item clicked, pick it up
    if (clickedSpawn) {
      const item = pickupItems.find(i => i.id === clickedSpawn.item_id);
      console.log('🎯 Clicked item:', item?.name);
      pickupItem(clickedSpawn.id);
      return;
    }
    
    // Check if clicked on a location
    let clickedLocation = null;
    if (gameConfig?.locations) {
      for (const loc of gameConfig.locations) {
        const locX = loc.marker_x || loc.position?.x || 0;
        const locY = loc.marker_y || loc.position?.y || 0;
        
        if (locX === 0 && locY === 0) continue;
        
        // Check if click is within 40px radius of location
        const distance = Math.sqrt(Math.pow(clickX - locX, 2) + Math.pow(clickY - locY, 2));
        if (distance < 40) {
          clickedLocation = loc;
          break;
        }
      }
    }
    
    // If location clicked, open modal
    if (clickedLocation) {
      console.log('🎯 Clicked location:', clickedLocation.name);
      handleLocationClick(clickedLocation, 'info');
      return; // Don't start dragging
    }
    
    // Otherwise, start dragging
    setIsDragging(true);
    setDragStart({
      x: e.clientX - rect.left - pan.x,
      y: e.clientY - rect.top - pan.y
    });
  };
  
  // Pickup item function (legacy map-based)
  const pickupItem = async (spawnId) => {
    try {
      const response = await axios.post(`${API_URL}/api/game/pickup-item`, null, {
        params: { user_id: userId, spawn_id: spawnId }
      });
      
      if (response.data.success) {
        const item = response.data.item;
        alert(`✅ Picked up ${item.emoji} ${item.name}! (+${response.data.new_quantity})`);
        
        // Remove spawn from list (will respawn later)
        setItemSpawns(prev => prev.filter(s => s.id !== spawnId));
        
        // Reload inventory
        const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
        setCollectedItems(progressRes.data.collected_items || []);
      }
    } catch (error) {
      console.error('Pickup error:', error);
      alert(error.response?.data?.detail || 'Failed to pickup item');
    }
  };

  // Load items available at current location
  const loadLocationItems = async (locationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/game/location-items/${locationId}`);
      const items = response.data.items || [];
      if (items.length > 0) {
        setLocationItems(items);
        setShowItemPickupModal(true);
      }
    } catch (error) {
      console.error('Error loading location items:', error);
    }
  };

  // Handle picking up a single item from location
  const handlePickupLocationItem = async (item) => {
    try {
      const response = await axios.post(`${API_URL}/api/game/pickup-location-item`, null, {
        params: {
          user_id: userId,
          location_id: currentLocation?.id || currentLocation,
          item_id: item.id,
          quantity: 1
        }
      });
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        
        // Update location items list
        setLocationItems(prev => prev.map(li => 
          li.id === item.id 
            ? {...li, quantity_available: li.quantity_available - 1}
            : li
        ).filter(li => li.quantity_available > 0));
        
        // Reload inventory
        const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
        setCollectedItems(progressRes.data.collected_items || []);
      }
    } catch (error) {
      console.error('Pickup error:', error);
      alert(error.response?.data?.detail || 'Failed to pickup item');
    }
  };

  // Handle picking up all items at once
  const handlePickupAllItems = async () => {
    for (const item of locationItems) {
      for (let i = 0; i < item.quantity_available; i++) {
        try {
          await axios.post(`${API_URL}/api/game/pickup-location-item`, null, {
            params: {
              user_id: userId,
              location_id: currentLocation?.id || currentLocation,
              item_id: item.id,
              quantity: 1
            }
          });
        } catch (error) {
          console.error('Pickup error:', error);
        }
      }
    }
    
    alert('🎒 Picked up all items!');
    setLocationItems([]);
    setShowItemPickupModal(false);
    
    // Reload inventory
    const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
    setCollectedItems(progressRes.data.collected_items || []);
  };

  // Load trade offers at location
  const loadTradeOffers = async (locationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/game/trade-offers/${locationId}`);
      setTradeOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error loading trade offers:', error);
    }
  };

  // Load delivery menu at location
  const loadDeliveryMenu = async (locationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/game/delivery-menu/${locationId}`);
      setDeliveryItems(response.data.items || []);
    } catch (error) {
      console.error('Error loading delivery menu:', error);
    }
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left - pan.x) / zoom;
    const mouseY = (e.clientY - rect.top - pan.y) / zoom;
    
    // If dragging a road node, update its position
    if (draggingNodeIndex !== null && gameConfig?.road_nodes) {
      const updatedNodes = [...gameConfig.road_nodes];
      updatedNodes[draggingNodeIndex] = {
        ...updatedNodes[draggingNodeIndex],
        x: Math.round(mouseX),
        y: Math.round(mouseY)
      };
      
      // Update gameConfig with new node position
      setGameConfig(prev => ({
        ...prev,
        road_nodes: updatedNodes
      }));
      
      // Send update to admin parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'ROAD_NODE_MOVED',
          index: draggingNodeIndex,
          x: Math.round(mouseX),
          y: Math.round(mouseY)
        }, '*');
      }
      return;
    }
    
    if (!isDragging) return;
    
    let newX = e.clientX - rect.left - dragStart.x;
    let newY = e.clientY - rect.top - dragStart.y;
    
    const canvas = canvasRef.current;
    
    // NEW LOGIC: At 100% zoom, ENABLE panning. At <100% zoom, DISABLE panning.
    if (zoom >= 1.0) {
      // At 100% zoom: Allow panning in all directions to explore for gems
      // Set generous limits to allow exploring the world
      const worldSize = canvas.width * 2; // Allow panning 2x canvas size
      const maxPan = worldSize;
      const minPan = -worldSize;
      newX = Math.max(minPan, Math.min(maxPan, newX));
      newY = Math.max(minPan, Math.min(maxPan, newY));
    } else if (zoom <= 0.5) {
      // At 50% zoom or lower: NO panning (camera shows more world automatically)
      newX = 0;
      newY = 0;
    } else {
      // Between 50% and 100%: Limited panning
      const maxPan = canvas.width * (1 - zoom) * 2;
      const minPan = -maxPan;
      newX = Math.max(minPan, Math.min(maxPan, newX));
      newY = Math.max(minPan, Math.min(maxPan, newY));
    }
    
    setPan({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    if (draggingNodeIndex !== null) {
      console.log(`📍 Finished dragging node #${draggingNodeIndex + 1}`);
      setDraggingNodeIndex(null);
    }
    setIsDragging(false);
  };

  // Zoom controls
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 1)); // Max 100%
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.2)); // Min 20%
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Auto zoom for taxi travel
  const zoomToTaxi = () => {
    setZoom(0.6); // 60% zoom when taxi travels
  };

  // Save Progress
  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/game/progress/${userId}`, {
        ...gameProgress,
        energy: Math.round(energy)
      });
      alert('✅ Progress saved!');
    } catch (error) {
      console.error('Error saving:', error);
      alert('❌ Save failed');
    }
  };

  // Daily Login Reward
  const claimDailyReward = () => {
    if (dailyLoginClaimed) return;
    
    const reward = 50;
    setGameProgress(prev => ({
      ...prev,
      gems_balance: prev.gems_balance + reward
    }));
    setDailyLoginClaimed(true);
    alert(`🎁 Daily login bonus: +${reward} gems!`);
  };

  // Calculate Rank
  useEffect(() => {
    if (!gameProgress) return;
    
    const balance = gameProgress.gems_balance;
    let rank = 'Beginner';
    let nextRank = 500;
    
    if (balance >= 5000) { rank = 'Legend'; nextRank = null; }
    else if (balance >= 2000) { rank = 'Expert'; nextRank = 5000; }
    else if (balance >= 1000) { rank = 'Advanced'; nextRank = 2000; }
    else if (balance >= 500) { rank = 'Intermediate'; nextRank = 1000; }
    
    setRankInfo({ rank, nextRank });
  }, [gameProgress]);

  // Smart Suggestions
  useEffect(() => {
    if (!gameProgress || !gameConfig) return;
    
    const balance = gameProgress.gems_balance;
    
    // Check if close to unlocking something
    if (balance >= 980 && balance < 1000) {
      setSmartSuggestion(`💡 You're ${1000 - balance} gems away from Advanced rank!`);
    } else if (balance >= 480 && balance < 500) {
      setSmartSuggestion(`💡 You're ${500 - balance} gems away from Intermediate rank!`);
    } else {
      setSmartSuggestion(null);
    }
  }, [gameProgress, gameConfig]);

  if (loading) {
    return (
      <div style={styles.loading}>
        <h2>🎮 Loading Morocco Journey...</h2>
      </div>
    );
  }

  // GAME NOT VISIBLE - Show hidden message
  if (!gameIsVisible) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '60px 40px',
          borderRadius: '20px',
          border: '2px solid rgba(255,255,255,0.2)',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🚧</div>
          <h1 style={{ fontSize: '28px', marginBottom: '15px', color: '#ffd700' }}>
            Game Coming Soon!
          </h1>
          <p style={{ fontSize: '16px', color: '#aaa', lineHeight: '1.6' }}>
            Morocco Journey is currently under development. 
            <br/>Check back soon for an amazing adventure!
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            style={{
              marginTop: '30px',
              padding: '15px 40px',
              fontSize: '16px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // GAME IS PRO - Show paywall for non-members
  if (gameIsPro && !userHasMembership) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        color: 'white',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.08)',
          padding: '60px 40px',
          borderRadius: '20px',
          border: '2px solid rgba(255,215,0,0.3)',
          maxWidth: '500px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
        }}>
          <div style={{ 
            fontSize: '80px', 
            marginBottom: '20px',
            filter: 'drop-shadow(0 0 20px rgba(255,215,0,0.5))'
          }}>🔒</div>
          <h1 style={{ 
            fontSize: '28px', 
            marginBottom: '10px', 
            background: 'linear-gradient(90deg, #ffd700, #ffaa00)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Pro Members Only
          </h1>
          <p style={{ fontSize: '16px', color: '#aaa', lineHeight: '1.6', marginBottom: '25px' }}>
            Morocco Journey is an exclusive experience for Pro members.
            <br/>Upgrade now to unlock this amazing adventure!
          </p>
          <div style={{
            background: 'linear-gradient(90deg, rgba(255,215,0,0.1), rgba(255,170,0,0.1))',
            padding: '15px 20px',
            borderRadius: '10px',
            marginBottom: '25px',
            border: '1px solid rgba(255,215,0,0.2)'
          }}>
            <p style={{ fontSize: '14px', color: '#ffd700', margin: 0 }}>
              ✨ Explore Morocco • 🚕 Ride Taxis • 💎 Collect Gems • 🗣️ Learn Darija
            </p>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => window.location.href = '/membership'}
              style={{
                padding: '15px 35px',
                fontSize: '16px',
                background: 'linear-gradient(90deg, #ffd700, #ffaa00)',
                color: '#1a1a2e',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(255,215,0,0.4)'
              }}
            >
              👑 Upgrade to Pro
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              style={{
                padding: '15px 35px',
                fontSize: '16px',
                backgroundColor: 'transparent',
                color: '#888',
                border: '2px solid #444',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Dynamic theme based on time of day
  const isDayTime = timeOfDay === 'day';
  const theme = {
    background: isDayTime ? '#f5f5f5' : '#1a1a2e',
    statsBar: isDayTime ? '#ffffff' : '#16213e',
    statsBorder: isDayTime ? '#e0e0e0' : '#0f3460',
    text: isDayTime ? '#333333' : '#ffffff',
    textSecondary: isDayTime ? '#666666' : '#aaaaaa',
    panel: isDayTime ? '#ffffff' : '#16213e',
    panelBorder: isDayTime ? '#e0e0e0' : '#0f3460',
    cardBg: isDayTime ? '#f9f9f9' : '#0f3460',
    buttonBg: isDayTime ? '#e0e0e0' : '#0f3460'
  };

  return (
    <div style={{...styles.container, backgroundColor: theme.background}}>
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div style={{
          backgroundColor: '#2196F3',
          color: 'white',
          padding: '10px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          borderBottom: '3px solid #1976D2'
        }}>
          👁️ LIVE PREVIEW MODE - Changes from admin editor appear here in real-time
        </div>
      )}

      {/* Top Stats Bar */}
      <div style={{
        ...styles.statsBar,
        backgroundColor: theme.statsBar,
        borderBottom: `2px solid ${theme.statsBorder}`,
        color: theme.text
      }}>
        <div style={{...styles.statItem, color: theme.text}}>
          <span style={styles.statIcon}>{timeOfDay === 'day' ? '☀️' : '🌙'}</span>
          <span>{timeOfDay === 'day' ? 'Day' : 'Night'}</span>
        </div>
        <div style={{...styles.statItem, color: theme.text}}>
          <span style={styles.statIcon}>⚡</span>
          <span>Energy: {Math.round(energy)}/100</span>
          <div style={styles.energyBar}>
            <div style={{ ...styles.energyFill, width: `${energy}%` }} />
          </div>
        </div>
        <div style={{...styles.statItem, color: theme.text}}>
          <span style={styles.statIcon}>💎</span>
          <span>{gameProgress?.gems_balance || 0} gems</span>
        </div>
        
        {/* Gender Selection Buttons */}
        <div style={{display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '15px'}}>
          <button 
            onClick={() => setSelectedGender('male')}
            style={{
              ...styles.genderBtn,
              backgroundColor: selectedGender === 'male' ? '#2196F3' : '#666',
              fontWeight: selectedGender === 'male' ? 'bold' : 'normal'
            }}
          >
            👨 Male
          </button>
          <button 
            onClick={() => setSelectedGender('female')}
            style={{
              ...styles.genderBtn,
              backgroundColor: selectedGender === 'female' ? '#E91E63' : '#666',
              fontWeight: selectedGender === 'female' ? 'bold' : 'normal'
            }}
          >
            👩 Female
          </button>
        </div>
        
        <button onClick={handleSave} style={styles.saveBtn}>
          💾 Save
        </button>
      </div>

      <div style={styles.gameArea}>
        {/* Canvas */}
        <div style={styles.canvasContainer}>
          <canvas
            ref={canvasRef}
            width={900}
            height={600}
            style={styles.canvas}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
          
          {/* Zoom Controls (Admin Only) */}
          {isAdmin && (
            <div style={styles.zoomControls}>
              <button onClick={handleZoomIn} style={styles.zoomBtn}>🔍+</button>
              <button onClick={handleResetZoom} style={styles.zoomBtn}>↺</button>
              <button onClick={handleZoomOut} style={styles.zoomBtn}>🔍-</button>
              <span style={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
            </div>
          )}
        </div>

        {/* Right Side Panel */}
        <div style={{
          ...styles.rightPanel,
          width: rightPanelOpen ? '350px' : '0px',
          backgroundColor: theme.panel,
          borderLeft: `2px solid ${theme.panelBorder}`
        }}>
          {rightPanelOpen && (
            <>
              {/* Panel Tabs - 4 Tab System */}
              <div style={{
                ...styles.panelTabs,
                backgroundColor: theme.buttonBg,
                borderBottom: `2px solid ${theme.panelBorder}`
              }}>
                <button
                  onClick={() => setActiveTab('locations')}
                  style={{
                    ...styles.panelTab,
                    color: activeTab === 'locations' ? theme.text : '#999',
                    backgroundColor: activeTab === 'locations' ? theme.panel : 'transparent',
                    ...(activeTab === 'locations' ? styles.panelTabActive : {}),
                    fontSize: '12px',
                    padding: '8px 6px'
                  }}
                >
                  📍 Map
                </button>
                <button
                  onClick={() => setActiveTab('rewards')}
                  style={{
                    ...styles.panelTab,
                    color: activeTab === 'rewards' ? theme.text : '#999',
                    backgroundColor: activeTab === 'rewards' ? theme.panel : 'transparent',
                    ...(activeTab === 'rewards' ? styles.panelTabActive : {}),
                    fontSize: '12px',
                    padding: '8px 6px'
                  }}
                >
                  🎁 Rewards
                </button>
                <button
                  onClick={() => setActiveTab('inventory')}
                  style={{
                    ...styles.panelTab,
                    color: activeTab === 'inventory' ? theme.text : '#999',
                    backgroundColor: activeTab === 'inventory' ? theme.panel : 'transparent',
                    ...(activeTab === 'inventory' ? styles.panelTabActive : {}),
                    fontSize: '12px',
                    padding: '8px 6px'
                  }}
                >
                  🎒 Items
                </button>
                <button
                  onClick={() => setActiveTab('pouch')}
                  style={{
                    ...styles.panelTab,
                    color: activeTab === 'pouch' ? theme.text : '#999',
                    backgroundColor: activeTab === 'pouch' ? theme.panel : 'transparent',
                    ...(activeTab === 'pouch' ? styles.panelTabActive : {}),
                    fontSize: '12px',
                    padding: '8px 6px'
                  }}
                >
                  💰 Pouch
                </button>
              </div>

              {/* Panel Content */}
              <div style={{...styles.panelContent, color: theme.text}}>
                
                {activeTab === 'locations' && (
                  <LocationsPanel 
                    locations={gameConfig?.locations || []}
                    currentLocation={currentLocation}
                    previousLocation={previousLocation}
                    selectedDestination={selectedDestination}
                    roads={roads}
                    onLocationClick={(location, buttonType) => handleLocationClick(location, buttonType)}
                    theme={theme}
                    isTaxiAnimating={isTaxiAnimating}
                    setShowTaxiConversation={setShowTaxiConversation}
                  />
                )}

                {activeTab === 'rewards' && (
                  <RewardsPanel
                    dailyLoginClaimed={dailyLoginClaimed}
                    claimDailyReward={claimDailyReward}
                    rankInfo={rankInfo}
                    balance={gameProgress?.gems_balance || 0}
                    gemFeatures={gameConfig?.gem_features || {}}
                    spendingLocked={spendingLocked}
                    setSpendingLocked={setSpendingLocked}
                    savingsGoal={savingsGoal}
                    smartSuggestion={smartSuggestion}
                    theme={theme}
                  />
                )}

                {activeTab === 'inventory' && (
                  <InventoryPanel
                    collectedItems={collectedItems}
                    pickupItems={pickupItems}
                    userId={userId}
                    setCollectedItems={setCollectedItems}
                    setGameProgress={setGameProgress}
                    theme={theme}
                  />
                )}

                {activeTab === 'pouch' && (
                  <PouchTradePanel
                    balance={gameProgress?.gems_balance || 0}
                    transactions={recentTransactions}
                    lowBalanceWarning={gameProgress?.gems_balance < 100}
                    theme={theme}
                    collectedItems={collectedItems}
                    pickupItems={pickupItems}
                    setShowMerchantModal={setShowMerchantModal}
                    setShowTradeModal={setShowTradeModal}
                    setShowDeliveryModal={setShowDeliveryModal}
                    userId={userId}
                    setCollectedItems={setCollectedItems}
                    setGameProgress={setGameProgress}
                    currentLocation={currentLocation}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Toggle Panel Button */}
        <button
          onClick={() => setRightPanelOpen(!rightPanelOpen)}
          style={{
            ...styles.togglePanelBtn,
            backgroundColor: theme.panel,
            color: theme.text,
            border: `2px solid ${theme.panelBorder}`
          }}
        >
          {rightPanelOpen ? '▶' : '◀'}
        </button>
      </div>

      {/* Item Pickup Modal - Shows when arriving at location with items */}
      {showItemPickupModal && locationItems.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '25px',
            borderRadius: '16px',
            maxWidth: '450px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '2px solid #5C6BC0'
          }}>
            <h2 style={{color: '#FFD700', margin: '0 0 5px 0', textAlign: 'center'}}>
              🎁 Items Found!
            </h2>
            <p style={{color: '#999', textAlign: 'center', marginBottom: '20px', fontSize: '14px'}}>
              You found items at this location. Pick what you want!
            </p>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px'}}>
              {locationItems.map((item, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#16213e',
                  padding: '15px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: item.rarity === 'rare' ? '2px solid #FFD700' : '1px solid #0f3460'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <span style={{fontSize: '36px'}}>{item.emoji}</span>
                    <div>
                      <div style={{color: 'white', fontWeight: 'bold'}}>{item.name}</div>
                      <div style={{color: '#999', fontSize: '12px'}}>
                        {item.category} • {item.rarity}
                        {item.can_eat && ` • +${item.energy_restore}% energy`}
                      </div>
                      <div style={{color: '#4CAF50', fontSize: '12px'}}>
                        Available: {item.quantity_available}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePickupLocationItem(item)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    Pick Up
                  </button>
                </div>
              ))}
            </div>
            
            <div style={{display: 'flex', gap: '10px'}}>
              <button
                onClick={() => setShowItemPickupModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Skip for Now
              </button>
              <button
                onClick={handlePickupAllItems}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#5C6BC0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                Pick All 🎒
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Taxi Bargaining Dialog */}
      {showTaxiBargaining && selectedDestination && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            border: '3px solid #FFD700'
          }}>
            <div style={{fontSize: '60px', marginBottom: '20px'}}>🚕</div>
            <h2 style={{color: '#FFD700', marginBottom: '10px'}}>Taxi to {selectedDestination.bubble_text || selectedDestination.name}</h2>
            
            <div style={{
              backgroundColor: '#0f3460',
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              <p style={{color: 'white', fontSize: '18px', marginBottom: '15px'}}>
                💬 <strong>Taxi Driver:</strong> "Where to, friend?"
              </p>
              <p style={{color: '#FFD700', fontSize: '16px', marginBottom: '10px'}}>
                📍 Destination: <strong>{selectedDestination.name}</strong>
              </p>
              <p style={{color: '#4CAF50', fontSize: '20px', fontWeight: 'bold'}}>
                💎 Price: {taxiPrice} gems
              </p>
              <p style={{color: '#888', fontSize: '14px', marginTop: '5px'}}>
                (Base: 50 gems + {gameConfig?.markers?.filter(m => m.route_id === selectedDestination.taxi_route_id).length} stops × 20 gems)
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={confirmTaxiTravel}
                disabled={isTaxiAnimating || (gameProgress?.gems_balance || 0) < taxiPrice}
                style={{
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: (gameProgress?.gems_balance || 0) >= taxiPrice ? '#4CAF50' : '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (gameProgress?.gems_balance || 0) >= taxiPrice ? 'pointer' : 'not-allowed',
                  flex: 1
                }}
              >
                ✅ {isTaxiAnimating ? 'Traveling...' : 'Let\'s Go!'}
              </button>
              <button
                onClick={() => setShowTaxiBargaining(false)}
                disabled={isTaxiAnimating}
                style={{
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: '#F44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                ❌ Cancel
              </button>
            </div>
            
            {(gameProgress?.gems_balance || 0) < taxiPrice && (
              <p style={{color: '#F44336', marginTop: '15px', fontWeight: 'bold'}}>
                ⚠️ Not enough gems! You have {gameProgress?.gems_balance || 0} but need {taxiPrice}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Speak to Taxi Modal (when clicking current location) */}
      {showTaxiConversation && currentLocation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            border: '3px solid #2196F3'
          }}>
            <div style={{fontSize: '60px', marginBottom: '20px'}}>🚕</div>
            <h2 style={{color: '#2196F3', marginBottom: '15px'}}>Taxi at {currentLocation.bubble_text || currentLocation.name}</h2>
            
            {/* Gems Balance Display */}
            <div style={{
              backgroundColor: '#FFD700',
              padding: '10px 20px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'inline-block'
            }}>
              <span style={{fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e'}}>
                💎 {gameProgress?.gems_balance || 0} Gems
              </span>
            </div>
            
            <div style={{
              backgroundColor: '#0f3460',
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              {/* Random Darija Phrase from Taxi Driver */}
              {currentPhrase ? (
                <div style={{marginBottom: '15px'}}>
                  <p style={{color: 'white', fontSize: '16px', marginBottom: '10px'}}>
                    🚕 <strong>Taxi Driver:</strong>
                  </p>
                  <div style={{
                    backgroundColor: '#1a1a2e',
                    padding: '15px',
                    borderRadius: '10px',
                    border: '2px solid #4CAF50'
                  }}>
                    <p style={{color: '#4CAF50', fontSize: '24px', fontWeight: 'bold', margin: '0 0 8px 0'}}>
                      "{currentPhrase.darija}"
                    </p>
                    <p style={{color: '#FFD700', fontSize: '16px', margin: '0 0 5px 0'}}>
                      = {currentPhrase.english}
                    </p>
                    <p style={{color: '#888', fontSize: '12px', margin: 0}}>
                      ({currentPhrase.category})
                    </p>
                  </div>
                </div>
              ) : (
                <p style={{color: 'white', fontSize: '18px', marginBottom: '15px', lineHeight: '1.6'}}>
                  💬 <strong>Taxi Driver:</strong> "Marhba! Welcome! Where to today?"
                </p>
              )}
              
              <p style={{color: '#FFD700', fontSize: '16px', marginBottom: '15px'}}>
                📍 <strong>Current:</strong> {currentLocation.bubble_text || currentLocation.name}
                {selectedDestination && (
                  <span style={{color: '#4CAF50'}}> → {selectedDestination.bubble_text || selectedDestination.name}</span>
                )}
              </p>
              
              {!selectedDestination && (
                <p style={{color: '#FF9800', fontSize: '14px', fontStyle: 'italic'}}>
                  💡 Select a destination from the Map tab first!
                </p>
              )}
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexDirection: 'column'
            }}>
              {/* Show different buttons based on bargaining state */}
              {!hasBargained ? (
                <>
                  {/* Must bargain first */}
                  <button
                    onClick={async () => {
                      if (!selectedDestination || selectedDestination.id === currentLocation?.id) {
                        alert('Please select a destination first from the Map tab!');
                        return;
                      }
                      
                      // Calculate DYNAMIC price based on distance (2-20 gems)
                      const fromId = currentLocation?.id || currentLocation;
                      const toId = selectedDestination?.id || selectedDestination;
                      
                      const { basePrice } = await calculateDynamicTaxiPrice(fromId, toId);
                      
                      // Moroccan bargaining: Taxi asks 2x, you can bargain down
                      const taxiAskingPrice = Math.min(20, Math.round(basePrice * 2)); // Taxi inflates 2x, max 20
                      const fairPrice = basePrice; // The actual fair price (2-20 gems)
                      const midPrice = Math.round((taxiAskingPrice + fairPrice) / 2); // Middle option
                      
                      // Price options: [fair/low, middle, taxi's asking price]
                      const prices = [fairPrice, midPrice, taxiAskingPrice];
                      
                      console.log(`💰 Bargain prices: fair=${fairPrice}, mid=${midPrice}, asking=${taxiAskingPrice}`);
                      
                      setTaxiInitialPrice(taxiAskingPrice);
                      setAvailablePrices(prices);
                      setTaxiPrice(fairPrice); // Set the actual fair price
                      setBargainStep(1);
                      setUserFirstOffer(null);
                      setShowTaxiConversation(false);
                      setShowBargainModal(true);
                    }}
                    disabled={!selectedDestination || selectedDestination.id === currentLocation?.id}
                    style={{
                      padding: '15px 30px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: (!selectedDestination || selectedDestination.id === currentLocation?.id) ? '#666' : '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (!selectedDestination || selectedDestination.id === currentLocation?.id) ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 6px rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    💰 Bargain Price {selectedDestination ? `to ${selectedDestination.name}` : ''}
                  </button>
                  
                  <p style={{color: '#FF9800', fontSize: '13px', margin: '5px 0', textAlign: 'center'}}>
                    ⚠️ You must agree on a price before driving!
                  </p>
                </>
              ) : (
                <>
                  {/* Already bargained - can start journey */}
                  <div style={{
                    backgroundColor: '#1B5E20',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '10px'
                  }}>
                    <p style={{color: '#4CAF50', margin: 0, fontSize: '16px'}}>
                      ✅ Price agreed: <strong>{finalPrice || taxiPrice} gems</strong> to {selectedDestination?.name}
                    </p>
                  </div>
                  
                  <button
                    onClick={async () => {
                      // Use the new System B continuous animation via confirmTaxiTravel
                      console.log('🚕 Start Journey clicked - using System B continuous animation');
                      confirmTaxiTravel();
                    }}
                    disabled={(gameProgress?.gems_balance || 0) < (finalPrice || taxiPrice || 5)}
                    style={{
                      padding: '18px 35px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      backgroundColor: (gameProgress?.gems_balance || 0) >= (finalPrice || taxiPrice || 5) ? '#2196F3' : '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (gameProgress?.gems_balance || 0) >= (finalPrice || taxiPrice || 5) ? 'pointer' : 'not-allowed',
                      boxShadow: '0 4px 6px rgba(33, 150, 243, 0.3)'
                    }}
                  >
                    🚕 Start Journey! ({finalPrice || taxiPrice || 5} 💎)
                  </button>
                  
                  {(gameProgress?.gems_balance || 0) < (finalPrice || taxiPrice || 5) && (
                    <p style={{color: '#f44336', fontSize: '14px', margin: '5px 0'}}>
                      ⚠️ Not enough gems! You have {gameProgress?.gems_balance || 0} but need {finalPrice || taxiPrice || 5}
                    </p>
                  )}
                </>
              )}
              
              {/* Cancel button */}
              <button
                onClick={() => {
                  setShowTaxiConversation(false);
                  setHasBargained(false);
                  setFinalPrice(0);
                }}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bargain Price Modal - Realistic Moroccan Style */}
      {showBargainModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '35px',
            borderRadius: '15px',
            maxWidth: '520px',
            width: '90%',
            textAlign: 'center',
            border: '3px solid #FFD700'
          }}>
            <div style={{fontSize: '70px', marginBottom: '15px'}}>🚕💬</div>
            
            {bargainStep === 1 ? (
              // STEP 1: Taxi shows price, user makes first offer
              <>
                <h2 style={{color: '#FFD700', marginBottom: '15px', fontSize: '24px'}}>
                  Negotiate Taxi Price
                </h2>
                
                <div style={{
                  backgroundColor: '#0f3460',
                  padding: '25px',
                  borderRadius: '10px',
                  marginBottom: '25px'
                }}>
                  <p style={{color: 'white', fontSize: '17px', marginBottom: '15px', lineHeight: '1.6'}}>
                    🚕 <strong>Taxi Driver:</strong><br/>
                    "To {selectedDestination?.bubble_text || selectedDestination?.name}, it will cost <strong style={{color: '#FFD700'}}>{taxiInitialPrice} gems</strong>."
                  </p>
                  <p style={{color: '#4CAF50', fontSize: '15px', marginTop: '12px'}}>
                    💡 Make your offer - How much will you pay?
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  {availablePrices.map((price, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setUserFirstOffer(price);
                        
                        if (price === taxiInitialPrice) {
                          // User accepted full price - no bargaining
                          setFinalPrice(price);
                          setHasBargained(true);
                          setShowBargainModal(false);
                          setShowTaxiConversation(true);
                        } else if (price === availablePrices[0]) {
                          // User offered lowest price - taxi will counter
                          setBargainStep(2);
                        } else {
                          // User offered middle price - taxi accepts!
                          setFinalPrice(price);
                          setHasBargained(true);
                          setBargainStep(3); // Success step
                        }
                      }}
                      style={{
                        padding: '18px 30px',
                        fontSize: '19px',
                        fontWeight: 'bold',
                        backgroundColor: index === 0 ? '#4CAF50' : (index === 1 ? '#FF9800' : '#2196F3'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.03)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      💎 {price} Gems {index === availablePrices.length - 1 && '(Full Price)'}
                    </button>
                  ))}
                </div>
              </>
            ) : bargainStep === 2 ? (
              // STEP 2: Taxi counters - user picks from 2 remaining prices
              <>
                <h2 style={{color: '#FF9800', marginBottom: '15px', fontSize: '24px'}}>
                  Taxi Driver's Counter Offer
                </h2>
                
                <div style={{
                  backgroundColor: '#0f3460',
                  padding: '25px',
                  borderRadius: '10px',
                  marginBottom: '25px'
                }}>
                  <p style={{color: 'white', fontSize: '17px', marginBottom: '15px', lineHeight: '1.6'}}>
                    🚕 <strong>Taxi Driver:</strong><br/>
                    "{userFirstOffer} gems? Ah, that's too low, friend!"
                  </p>
                  <p style={{color: '#FFD700', fontSize: '16px', fontWeight: 'bold'}}>
                    "Please, I need at least {availablePrices[1]} gems. Pick one of these:"
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  {availablePrices.slice(1).map((price, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setFinalPrice(price);
                        setHasBargained(true);
                        setBargainStep(3);
                      }}
                      style={{
                        padding: '18px 30px',
                        fontSize: '19px',
                        fontWeight: 'bold',
                        backgroundColor: index === 0 ? '#FF9800' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.03)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      💎 {price} Gems
                    </button>
                  ))}
                </div>
              </>
            ) : (
              // STEP 3: Success! Deal made
              <>
                <h2 style={{color: '#4CAF50', marginBottom: '15px', fontSize: '26px'}}>
                  ✅ Deal Made!
                </h2>
                
                <div style={{
                  backgroundColor: '#0f3460',
                  padding: '25px',
                  borderRadius: '10px',
                  marginBottom: '25px'
                }}>
                  <p style={{color: 'white', fontSize: '18px', marginBottom: '15px', lineHeight: '1.6'}}>
                    🚕 <strong>Taxi Driver:</strong><br/>
                    "Thank you! Let's go!"
                  </p>
                  <div style={{
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '15px'
                  }}>
                    <p style={{color: '#FFD700', fontSize: '17px', fontWeight: 'bold'}}>
                      💰 Final Price: {finalPrice} gems
                    </p>
                    {finalPrice < taxiInitialPrice && (
                      <p style={{color: '#4CAF50', fontSize: '15px', marginTop: '8px'}}>
                        🎉 You saved {taxiInitialPrice - finalPrice} gems!
                      </p>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowBargainModal(false);
                    setBargainStep(1); // Reset for next time
                    setShowTaxiConversation(true);
                  }}
                  style={{
                    padding: '18px 30px',
                    fontSize: '19px',
                    fontWeight: 'bold',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    width: '100%',
                    boxShadow: '0 4px 6px rgba(76, 175, 80, 0.3)'
                  }}
                >
                  ✅ Great! Continue
                </button>
              </>
            )}
            
            {bargainStep < 3 && (
              <button
                onClick={() => {
                  setShowBargainModal(false);
                  setBargainStep(1);
                  setShowTaxiConversation(true);
                }}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: '10px'
                }}
              >
                ⬅️ Back
              </button>
            )}
          </div>
        </div>
      )}

      {/* Location Info Modal */}
      {showLocationInfoModal && infoLocation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '35px',
            borderRadius: '15px',
            maxWidth: '550px',
            width: '90%',
            textAlign: 'center',
            border: '3px solid #FF9800'
          }}>
            <div style={{fontSize: '70px', marginBottom: '15px'}}>📍</div>
            
            <h2 style={{color: '#FF9800', marginBottom: '15px', fontSize: '26px'}}>
              {infoLocation.bubble_text || infoLocation.name}
            </h2>
            
            {/* Show if this is current location */}
            {currentLocation && infoLocation.id === currentLocation.id && (
              <div style={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                padding: '10px',
                borderRadius: '8px',
                marginBottom: '15px',
                borderLeft: '4px solid #4CAF50'
              }}>
                <p style={{color: '#4CAF50', fontSize: '14px', margin: 0, fontWeight: 'bold'}}>
                  ✅ You are currently here
                </p>
              </div>
            )}
            
            <div style={{
              backgroundColor: '#0f3460',
              padding: '25px',
              borderRadius: '10px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <p style={{color: '#FFD700', fontSize: '16px', marginBottom: '15px', fontWeight: 'bold'}}>
                📖 About This Location:
              </p>
              <p style={{
                color: 'white',
                fontSize: '15px',
                lineHeight: '1.8',
                marginBottom: '15px'
              }}>
                {infoLocation.description || 'A beautiful place in Morocco waiting to be explored! (Admin will add description)'}
              </p>
              
              <div style={{
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                padding: '15px',
                borderRadius: '8px',
                borderLeft: '4px solid #FF9800'
              }}>
                <p style={{color: '#BBB', fontSize: '14px', marginBottom: '8px'}}>
                  👥 <strong>Who's here:</strong>
                </p>
                <p style={{color: '#CCC', fontSize: '13px', lineHeight: '1.6'}}>
                  {infoLocation.characters_info || 'Various interesting people you can meet and learn from! (Admin will add details)'}
                </p>
              </div>
            </div>
            
            {/* Delivery Button - Only show if at current location */}
            {currentLocation && infoLocation.id === currentLocation.id && (
              <button
                onClick={() => {
                  alert('📦 Delivery system coming soon!');
                }}
                style={{
                  padding: '15px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  marginBottom: '10px',
                  boxShadow: '0 4px 6px rgba(33, 150, 243, 0.3)'
                }}
              >
                📦 Delivery Item to This Location
              </button>
            )}
            
            <button
              onClick={() => {
                setShowLocationInfoModal(false);
                setInfoLocation(null);
              }}
              style={{
                padding: '15px 30px',
                fontSize: '18px',
                fontWeight: 'bold',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                width: '100%',
                boxShadow: '0 4px 6px rgba(76, 175, 80, 0.3)'
              }}
            >
              ✅ Got It!
            </button>
          </div>
        </div>
      )}

      {/* REMOVED: Old Stop Modal and Note Modal (dead System A UI) */}
      {/* Now using smooth continuous animation - taxi drives without stops */}

      {/* Arrival Modal - Congratulations + Payment Summary */}
      {showArrivalModal && selectedDestination && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '40px',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            border: '3px solid #4CAF50'
          }}>
            <div style={{fontSize: '80px', marginBottom: '20px'}}>🎉</div>
            <h2 style={{color: '#4CAF50', marginBottom: '10px', fontSize: '28px'}}>
              Congratulations!
            </h2>
            <h3 style={{color: '#FFD700', marginBottom: '20px', fontSize: '24px'}}>
              Welcome to {selectedDestination.bubble_text || selectedDestination.name}
            </h3>
            
            <div style={{
              backgroundColor: '#0f3460',
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              <p style={{color: 'white', fontSize: '16px', marginBottom: '15px'}}>
                🚕 You've arrived safely!
              </p>
              
              {/* Payment Summary */}
              <div style={{
                backgroundColor: '#1a1a2e',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '15px'
              }}>
                <h4 style={{color: '#FFD700', margin: '0 0 10px 0', fontSize: '16px'}}>
                  💳 Journey Payment
                </h4>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '18px'
                }}>
                  <span>Taxi Fare:</span>
                  <span style={{color: '#f44336', fontWeight: 'bold'}}>
                    -{taxiPrice} 💎
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '16px',
                  marginTop: '10px',
                  paddingTop: '10px',
                  borderTop: '1px solid #333'
                }}>
                  <span>New Balance:</span>
                  <span style={{color: '#4CAF50', fontWeight: 'bold'}}>
                    {gameProgress?.gems_balance || 0} 💎
                  </span>
                </div>
              </div>
              
              {/* Location Actions */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                marginTop: '15px'
              }}>
                {locationItems.length > 0 && (
                  <button
                    onClick={() => {
                      setShowArrivalModal(false);
                      setShowItemPickupModal(true);
                    }}
                    style={{
                      padding: '12px',
                      backgroundColor: '#FF9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}
                  >
                    🎁 Collect Items ({locationItems.length})
                  </button>
                )}
                
                {tradeOffers.length > 0 && (
                  <button
                    onClick={() => {
                      setShowArrivalModal(false);
                      setShowTradeModal(true);
                    }}
                    style={{
                      padding: '12px',
                      backgroundColor: '#5C6BC0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}
                  >
                    🔄 Trade Items ({tradeOffers.length})
                  </button>
                )}
                
                {deliveryItems.length > 0 && (
                  <button
                    onClick={() => {
                      setShowArrivalModal(false);
                      setShowDeliveryModal(true);
                    }}
                    style={{
                      padding: '12px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}
                  >
                    🚚 Order Delivery
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setShowArrivalModal(false);
                    setShowMerchantModal(true);
                  }}
                  style={{
                    padding: '12px',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '13px'
                  }}
                >
                  💰 Sell Items
                </button>
              </div>
            </div>
            
            <button
              onClick={() => {
                setShowArrivalModal(false);
              }}
              style={{
                padding: '15px 30px',
                fontSize: '18px',
                fontWeight: 'bold',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                width: '100%',
                marginTop: '10px'
              }}
            >
              ✅ Continue Exploring
            </button>
          </div>
        </div>
      )}

      {/* In-Game Guide/Assistant - Avatar Icon (Bottom-Right) */}
      {!showGuide && (
        <div
          onClick={() => setShowGuide(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            backgroundColor: '#4CAF50',
            border: '4px solid #fff',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '35px',
            zIndex: 9999,
            transition: 'transform 0.2s',
            animation: 'pulse 2s infinite'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          👨‍🏫
        </div>
      )}

      {/* Guide Speech Bubble */}
      {showGuide && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '350px',
          backgroundColor: '#fff',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          border: '3px solid #4CAF50',
          zIndex: 10000
        }}>
          {/* Close Button */}
          <button
            onClick={() => {
              setShowGuide(false);
              setGuideChoice(null);
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '25px',
              height: '25px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>

          {/* Speech Bubble Tail */}
          <div style={{
            position: 'absolute',
            bottom: '-15px',
            right: '30px',
            width: 0,
            height: 0,
            borderLeft: '15px solid transparent',
            borderRight: '15px solid transparent',
            borderTop: '15px solid #4CAF50'
          }}></div>

          {/* Avatar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '15px',
            gap: '10px'
          }}>
            <div style={{fontSize: '40px'}}>👨‍🏫</div>
            <div>
              <h3 style={{margin: 0, color: '#333', fontSize: '18px'}}>Your Guide</h3>
              <p style={{margin: 0, color: '#666', fontSize: '12px'}}>Here to help!</p>
            </div>
          </div>

          {/* Guide Message */}
          {!guideChoice && (
            <>
              <p style={{color: '#333', fontSize: '15px', marginBottom: '15px', lineHeight: '1.5'}}>
                👋 What can I do for you? Did you get stuck?
              </p>

              {/* 4 Choice Buttons */}
              <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                <button
                  onClick={() => setGuideChoice('how-to-play')}
                  style={{
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  📚 Show me how the game works
                </button>

                <button
                  onClick={() => setGuideChoice('navigation')}
                  style={{
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  🗺️ How do I get from location to location?
                </button>

                <button
                  onClick={() => setGuideChoice('gems')}
                  style={{
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  💎 Tips for finding gems/coins
                </button>

                <button
                  onClick={() => setGuideChoice('other')}
                  style={{
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  ❓ Other help or hints
                </button>
              </div>
            </>
          )}

          {/* Guide Response (Placeholder for later) */}
          {guideChoice && (
            <>
              <p style={{color: '#333', fontSize: '14px', lineHeight: '1.6', marginBottom: '15px'}}>
                {guideChoice === 'how-to-play' && '📚 Game mechanics explanation will be added here...'}
                {guideChoice === 'navigation' && '🗺️ Navigation help will be added here...'}
                {guideChoice === 'gems' && '💎 Gem finding tips will be added here...'}
                {guideChoice === 'other' && '❓ Additional help will be added here...'}
              </p>

              <button
                onClick={() => setGuideChoice(null)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                ⬅️ Back to choices
              </button>
            </>
          )}
        </div>
      )}
      
      {/* Conversation Modal - MUST be inside main Game component */}
      {showConversationModal && currentNode && conversationCharacter && (
        <div style={styles.modalOverlay} onClick={() => {}}>
          <div style={styles.conversationModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.conversationHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{
                  fontSize: '48px',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '50%'
                }}>
                  {conversationCharacter.emoji || '👤'}
                </div>
                <div>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: '24px' }}>
                    {conversationCharacter.name}
                  </h2>
                  <p style={{ margin: '5px 0 0 0', color: '#aaa', fontSize: '14px' }}>
                    {activeConversation.title}
                  </p>
                </div>
              </div>
              <button
                onClick={closeConversation}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '5px 10px'
                }}
              >
                ×
              </button>
            </div>

            <div style={styles.conversationContent}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '20px',
                borderRadius: '15px',
                marginBottom: '25px',
                color: '#fff',
                fontSize: '16px',
                lineHeight: '1.6',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}>
                <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
                  {currentNode.character_emotion === 'happy' && '😊'}
                  {currentNode.character_emotion === 'excited' && '🤩'}
                  {currentNode.character_emotion === 'sad' && '😢'}
                  {currentNode.character_emotion === 'angry' && '😠'}
                  {currentNode.character_emotion === 'neutral' && '😐'}
                  {' '}{conversationCharacter.name}
                </div>
                {currentNode.character_message}
              </div>

              {currentNode.gives_gems && currentNode.gives_gems > 0 && (
                <div style={{
                  background: 'rgba(255, 215, 0, 0.1)',
                  border: '2px solid gold',
                  borderRadius: '10px',
                  padding: '12px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  color: 'gold',
                  fontWeight: 'bold'
                }}>
                  💎 Complete this to earn {currentNode.gives_gems} gems!
                </div>
              )}

              <div style={{ marginTop: '20px' }}>
                <p style={{ color: '#aaa', marginBottom: '15px', fontSize: '14px' }}>
                  What would you like to say?
                </p>
                {currentNode.choices && currentNode.choices.map((choice) => (
                  <button
                    key={choice.id}
                    onClick={() => handleConversationChoice(choice)}
                    style={{
                      width: '100%',
                      padding: '15px 20px',
                      marginBottom: '12px',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '15px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                      fontWeight: '500'
                    }}
                  >
                    💬 {choice.text}
                  </button>
                ))}
              </div>

              <button
                onClick={closeConversation}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginTop: '20px',
                  background: 'transparent',
                  color: '#aaa',
                  border: '2px solid #555',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Skip Conversation
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Hotel/Restaurant Location Modal */}
      {showLocationModal && selectedLocation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10002
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '3px solid #5C6BC0'
          }}>
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{fontSize: '60px', marginBottom: '10px'}}>
                {selectedLocation.location_type === 'hotel' ? '🏨' : '🍽️'}
              </div>
              <h2 style={{color: '#5C6BC0', margin: '0 0 5px 0'}}>
                {selectedLocation.name}
              </h2>
              <p style={{color: '#999', margin: 0}}>
                {selectedLocation.bubble_text || selectedLocation.name}
              </p>
            </div>

            <div style={{
              display: 'flex',
              borderBottom: '2px solid #0f3460',
              marginBottom: '20px',
              gap: '5px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setLocationModalTab('info')}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: '12px 8px',
                  backgroundColor: locationModalTab === 'info' ? '#5C6BC0' : 'transparent',
                  color: locationModalTab === 'info' ? 'white' : '#999',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: locationModalTab === 'info' ? 'bold' : 'normal'
                }}
              >
                📋 Info
              </button>
              
              {/* HOTEL TABS */}
              {selectedLocation.location_type === 'hotel' && selectedLocation.hotel_data && (
                <>
                  <button
                    onClick={() => setLocationModalTab('rooms')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'rooms' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'rooms' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'rooms' ? 'bold' : 'normal'
                    }}
                  >
                    🏨 Rooms
                  </button>
                  <button
                    onClick={() => setLocationModalTab('receptionist')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'receptionist' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'receptionist' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'receptionist' ? 'bold' : 'normal'
                    }}
                  >
                    💬 Receptionist
                  </button>
                </>
              )}
              
              {/* RESTAURANT TABS */}
              {selectedLocation.location_type === 'restaurant' && selectedLocation.restaurant_data && (
                <>
                  <button
                    onClick={() => setLocationModalTab('menu')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'menu' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'menu' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'menu' ? 'bold' : 'normal'
                    }}
                  >
                    🍽️ Menu
                  </button>
                  <button
                    onClick={() => setLocationModalTab('waiter')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'waiter' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'waiter' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'waiter' ? 'bold' : 'normal'
                    }}
                  >
                    💬 Waiter
                  </button>
                </>
              )}
              
              {/* GYM TABS */}
              {selectedLocation.location_type === 'gym' && selectedLocation.gym_data && (
                <>
                  <button
                    onClick={() => setLocationModalTab('workout')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'workout' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'workout' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'workout' ? 'bold' : 'normal'
                    }}
                  >
                    🏋️ Workout
                  </button>
                  <button
                    onClick={() => setLocationModalTab('trainer')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'trainer' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'trainer' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'trainer' ? 'bold' : 'normal'
                    }}
                  >
                    💪 Trainer
                  </button>
                </>
              )}
              
              {/* LIBRARY TABS */}
              {selectedLocation.location_type === 'library' && (
                <>
                  <button
                    onClick={() => setLocationModalTab('books')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'books' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'books' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'books' ? 'bold' : 'normal'
                    }}
                  >
                    📚 Books
                  </button>
                  <button
                    onClick={() => setLocationModalTab('lessons')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'lessons' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'lessons' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'lessons' ? 'bold' : 'normal'
                    }}
                  >
                    🎓 Darija Lessons
                  </button>
                  <button
                    onClick={() => setLocationModalTab('librarian')}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      padding: '12px 8px',
                      backgroundColor: locationModalTab === 'librarian' ? '#5C6BC0' : 'transparent',
                      color: locationModalTab === 'librarian' ? 'white' : '#999',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: locationModalTab === 'librarian' ? 'bold' : 'normal'
                    }}
                  >
                    📖 Librarian
                  </button>
                </>
              )}
              
              <button
                onClick={() => setLocationModalTab('notes')}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: '12px 8px',
                  backgroundColor: locationModalTab === 'notes' ? '#5C6BC0' : 'transparent',
                  color: locationModalTab === 'notes' ? 'white' : '#999',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: locationModalTab === 'notes' ? 'bold' : 'normal'
                }}
              >
                ✏️ Notes
              </button>
            </div>

            {locationModalTab === 'info' && (
              <div>
                <p style={{color: 'white', lineHeight: '1.8'}}>
                  {selectedLocation.description || 'A wonderful place to visit!'}
                </p>
                {selectedLocation.location_type === 'hotel' && selectedLocation.hotel_data?.amenities && (
                  <div style={{backgroundColor: '#0f3460', padding: '15px', borderRadius: '8px', marginTop: '15px'}}>
                    <h4 style={{color: '#FFD700', margin: '0 0 10px 0'}}>✨ Amenities:</h4>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                      {selectedLocation.hotel_data.amenities.map((amenity, idx) => (
                        <span key={idx} style={{backgroundColor: '#1a1a2e', padding: '5px 10px', borderRadius: '5px', color: 'white', fontSize: '13px'}}>
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{marginTop: '20px', color: '#4CAF50', fontSize: '14px'}}>
                  💚 Energy: {selectedLocation.location_type === 'hotel' ? '100%' : '+10%'}
                </div>
              </div>
            )}

            {locationModalTab === 'rooms' && selectedLocation.hotel_data && (
              <div>
                <h3 style={{color: '#FFD700', marginTop: 0}}>Available Rooms</h3>
                {selectedLocation.hotel_data.rooms.map((room) => (
                  <div key={room.id} style={{backgroundColor: '#0f3460', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <div style={{flex: 1}}>
                        <h4 style={{color: 'white', margin: '0 0 5px 0'}}>{room.type}</h4>
                        <p style={{color: '#999', margin: 0, fontSize: '13px'}}>{room.description || 'Comfortable'}</p>
                      </div>
                      <div style={{textAlign: 'right', marginLeft: '20px'}}>
                        <div style={{fontSize: '24px', color: '#FFD700', fontWeight: 'bold'}}>{room.price} 💎</div>
                        <button
                          onClick={() => { setSelectedRoom(room); setLocationModalTab('book'); }}
                          disabled={room.available <= 0}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: room.available > 0 ? '#4CAF50' : '#666',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: room.available > 0 ? 'pointer' : 'not-allowed',
                            fontSize: '13px',
                            marginTop: '10px'
                          }}
                        >
                          {room.available > 0 ? 'Book' : 'Sold Out'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {locationModalTab === 'book' && selectedRoom && (
              <div style={{textAlign: 'center'}}>
                <h3 style={{color: '#FFD700'}}>Confirm Booking</h3>
                <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '8px', marginBottom: '20px'}}>
                  <h4 style={{color: 'white'}}>{selectedRoom.type} Room</h4>
                  <div style={{fontSize: '32px', color: '#FFD700', fontWeight: 'bold', margin: '15px 0'}}>
                    {selectedRoom.price} 💎
                  </div>
                </div>
                <div style={{display: 'flex', gap: '10px'}}>
                  <button
                    onClick={() => bookHotelRoom(selectedRoom)}
                    style={{flex: 1, padding: '15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer'}}
                  >
                    ✅ Confirm
                  </button>
                  <button
                    onClick={() => setLocationModalTab('rooms')}
                    style={{flex: 1, padding: '15px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer'}}
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            )}



            {/* RESTAURANT MENU TAB */}
            {locationModalTab === 'menu' && selectedLocation.restaurant_data && (
              <div>
                <h3 style={{color: '#FFD700', marginTop: 0}}>🍽️ Menu</h3>
                {selectedLocation.restaurant_data.menu.map((item) => (
                  <div key={item.id} style={{backgroundColor: '#0f3460', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '2px solid #333'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div style={{flex: 1}}>
                        <h4 style={{color: 'white', margin: '0 0 5px 0'}}>{item.name}</h4>
                        <p style={{color: '#999', margin: 0, fontSize: '13px'}}>{item.description || 'Delicious traditional dish'}</p>
                        <div style={{color: '#4CAF50', fontSize: '12px', marginTop: '5px'}}>
                          💚 Energy +{item.energy_bonus || 10}%
                        </div>
                      </div>
                      <div style={{textAlign: 'right', marginLeft: '20px'}}>
                        <div style={{fontSize: '24px', color: '#FFD700', fontWeight: 'bold', marginBottom: '10px'}}>
                          {item.price} 💎
                        </div>
                        <button
                          onClick={async () => {
                            const currentGems = gameProgress?.gems_balance || 0;
                            if (currentGems < item.price) {
                              alert('❌ Not enough gems!');
                              return;
                            }
                            
                            // Deduct gems
                            setGameProgress(prev => ({
                              ...prev,
                              gems_balance: currentGems - item.price,
                              energy: Math.min(100, (prev.energy || 100) + item.energy_bonus)
                            }));
                            
                            // Add transaction
                            setRecentTransactions(prev => [
                              {item: `Ordered ${item.name}`, amount: -item.price, timestamp: new Date()},
                              ...prev
                            ]);
                            
                            alert(`✅ Ordered ${item.name}! Energy +${item.energy_bonus}%`);
                          }}
                          disabled={!item.available}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: item.available ? '#4CAF50' : '#666',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: item.available ? 'pointer' : 'not-allowed',
                            fontSize: '13px',
                            fontWeight: 'bold'
                          }}
                        >
                          {item.available ? '🛒 Order' : 'Unavailable'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* SPEAK TO RECEPTIONIST TAB */}
            {locationModalTab === 'receptionist' && (
              <div style={{textAlign: 'center', padding: '20px'}}>
                <div style={{fontSize: '60px', marginBottom: '20px'}}>🧑‍💼</div>
                <h3 style={{color: '#FFD700', marginBottom: '15px'}}>Hotel Receptionist</h3>
                <p style={{color: 'white', marginBottom: '20px'}}>
                  Need assistance? Our receptionist can help you with:
                </p>
                <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left'}}>
                  <div style={{color: 'white', lineHeight: '2'}}>
                    <div>✅ Room service</div>
                    <div>✅ Local recommendations</div>
                    <div>✅ Transportation booking</div>
                    <div>✅ General inquiries</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const characterId = selectedLocation.hotel_data?.receptionist_character_id;
                    if (characterId) {
                      console.log('Opening conversation with receptionist:', characterId);
                      // Find the receptionist character and start conversation
                      const receptionistChar = gameConfig.characters?.find(c => c.id === characterId);
                      if (receptionistChar) {
                        setShowLocationModal(false);
                        startConversation(receptionistChar);
                      } else {
                        alert('⚠️ Receptionist character not found!');
                      }
                    } else {
                      alert('⚠️ Receptionist character needs to be configured in admin panel first!');
                    }
                  }}
                  style={{
                    padding: '15px 30px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  💬 Start Conversation
                </button>
              </div>
            )}

            {/* SPEAK TO WAITER TAB */}
            {locationModalTab === 'waiter' && (
              <div style={{textAlign: 'center', padding: '20px'}}>
                <div style={{fontSize: '60px', marginBottom: '20px'}}>🧑‍🍳</div>
                <h3 style={{color: '#FFD700', marginBottom: '15px'}}>Restaurant Waiter</h3>
                <p style={{color: 'white', marginBottom: '20px'}}>
                  Our waiter can help you with:
                </p>
                <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left'}}>
                  <div style={{color: 'white', lineHeight: '2'}}>
                    <div>✅ Menu recommendations</div>
                    <div>✅ Special dietary requests</div>
                    <div>✅ Daily specials</div>
                    <div>✅ Moroccan food culture</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const characterId = selectedLocation.restaurant_data?.waiter_character_id;
                    if (characterId) {
                      console.log('Opening conversation with waiter:', characterId);
                      // Find the waiter character and start conversation
                      const waiterChar = gameConfig.characters?.find(c => c.id === characterId);
                      if (waiterChar) {
                        setShowLocationModal(false);
                        startConversation(waiterChar);
                      } else {
                        alert('⚠️ Waiter character not found!');
                      }
                    } else {
                      alert('⚠️ Waiter character needs to be configured in admin panel first!');
                    }
                  }}
                  style={{
                    padding: '15px 30px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  💬 Start Conversation
                </button>
              </div>
            )}

            {/* GYM WORKOUT TAB */}
            {locationModalTab === 'workout' && selectedLocation.gym_data && (
              <div style={{textAlign: 'center', padding: '20px'}}>
                <div style={{fontSize: '60px', marginBottom: '20px'}}>🏋️</div>
                <h3 style={{color: '#FFD700', marginBottom: '15px'}}>Workout Options</h3>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px'}}>
                  <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '10px'}}>
                    <div style={{fontSize: '30px', marginBottom: '10px'}}>🏃</div>
                    <h4 style={{color: 'white', margin: '0 0 10px 0'}}>Cardio</h4>
                    <p style={{color: '#a0a0a0', fontSize: '13px', margin: '0 0 10px 0'}}>Treadmills, bikes, rowing</p>
                    <div style={{color: '#4CAF50'}}>-{selectedLocation.gym_data.energy_cost || 30} Energy</div>
                    <div style={{color: '#FFD700', marginTop: '5px'}}>+15 Gems</div>
                  </div>
                  <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '10px'}}>
                    <div style={{fontSize: '30px', marginBottom: '10px'}}>💪</div>
                    <h4 style={{color: 'white', margin: '0 0 10px 0'}}>Weights</h4>
                    <p style={{color: '#a0a0a0', fontSize: '13px', margin: '0 0 10px 0'}}>Free weights, machines</p>
                    <div style={{color: '#4CAF50'}}>-{selectedLocation.gym_data.energy_cost || 30} Energy</div>
                    <div style={{color: '#FFD700', marginTop: '5px'}}>+20 Gems</div>
                  </div>
                </div>
                
                <div style={{backgroundColor: '#1a1a2e', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                  <div style={{color: '#a0a0a0', fontSize: '14px'}}>
                    <span style={{color: 'white'}}>Daily Pass:</span> {selectedLocation.gym_data.daily_pass_price || 50} 💎
                    <span style={{marginLeft: '20px', color: 'white'}}>Membership:</span> {selectedLocation.gym_data.membership_price || 200} 💎/month
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    alert('🏋️ You started a workout! Great job!');
                  }}
                  style={{
                    padding: '15px 30px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  🏃 Start Workout
                </button>
              </div>
            )}

            {/* GYM TRAINER TAB */}
            {locationModalTab === 'trainer' && (
              <div style={{textAlign: 'center', padding: '20px'}}>
                <div style={{fontSize: '60px', marginBottom: '20px'}}>🧑‍🏫</div>
                <h3 style={{color: '#FFD700', marginBottom: '15px'}}>Personal Trainer</h3>
                <p style={{color: 'white', marginBottom: '20px'}}>
                  Our trainer can help you with:
                </p>
                <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left'}}>
                  <div style={{color: 'white', lineHeight: '2'}}>
                    <div>✅ Workout routines</div>
                    <div>✅ Fitness tips</div>
                    <div>✅ Nutrition advice</div>
                    <div>✅ Learn Darija fitness words</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const characterId = selectedLocation.gym_data?.trainer_character_id;
                    if (characterId) {
                      const trainerChar = gameConfig.characters?.find(c => c.id === characterId);
                      if (trainerChar) {
                        setShowLocationModal(false);
                        startConversation(trainerChar);
                      } else {
                        alert('⚠️ Trainer character not found!');
                      }
                    } else {
                      alert('⚠️ Trainer character needs to be configured in admin panel first!');
                    }
                  }}
                  style={{
                    padding: '15px 30px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  💬 Talk to Trainer
                </button>
              </div>
            )}

            {/* LIBRARY BOOKS TAB */}
            {locationModalTab === 'books' && (
              <div style={{padding: '10px'}}>
                <h3 style={{color: '#FFD700', marginBottom: '15px', textAlign: 'center'}}>📚 Available Books</h3>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px'}}>
                  {[
                    { title: 'Darija for Beginners', category: 'Language', gems: 5 },
                    { title: 'Moroccan History', category: 'History', gems: 3 },
                    { title: 'Moroccan Cooking', category: 'Food', gems: 4 },
                    { title: 'Tales of the Medina', category: 'Stories', gems: 6 },
                    { title: 'Atlas Mountains Guide', category: 'Travel', gems: 5 },
                    { title: 'Berber Culture', category: 'Culture', gems: 4 }
                  ].map((book, idx) => (
                    <div key={idx} style={{backgroundColor: '#0f3460', padding: '15px', borderRadius: '8px'}}>
                      <div style={{fontSize: '24px', marginBottom: '5px'}}>📖</div>
                      <h4 style={{color: 'white', margin: '0 0 5px 0', fontSize: '14px'}}>{book.title}</h4>
                      <div style={{color: '#a0a0a0', fontSize: '12px'}}>{book.category}</div>
                      <div style={{color: '#FFD700', fontSize: '12px', marginTop: '5px'}}>Read: +{book.gems} 💎</div>
                    </div>
                  ))}
                </div>
                
                <div style={{backgroundColor: '#1a1a2e', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
                  <p style={{color: '#a0a0a0', margin: '0', fontSize: '13px'}}>
                    📚 Reading books helps you learn Darija and earn gems!
                  </p>
                </div>
              </div>
            )}

            {/* LIBRARY DARIJA LESSONS TAB */}
            {locationModalTab === 'lessons' && (
              <div style={{textAlign: 'center', padding: '20px'}}>
                <div style={{fontSize: '60px', marginBottom: '20px'}}>🎓</div>
                <h3 style={{color: '#FFD700', marginBottom: '15px'}}>Darija Lessons</h3>
                
                <div style={{display: 'grid', gap: '10px', marginBottom: '20px'}}>
                  {[
                    { name: 'Greetings', words: 5, cost: 10 },
                    { name: 'Numbers', words: 10, cost: 15 },
                    { name: 'Food & Drink', words: 8, cost: 12 },
                    { name: 'Directions', words: 6, cost: 10 },
                    { name: 'Shopping', words: 7, cost: 12 }
                  ].map((lesson, idx) => (
                    <div key={idx} style={{backgroundColor: '#0f3460', padding: '15px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div style={{textAlign: 'left'}}>
                        <h4 style={{color: 'white', margin: '0 0 5px 0'}}>{lesson.name}</h4>
                        <div style={{color: '#a0a0a0', fontSize: '12px'}}>{lesson.words} words to learn</div>
                      </div>
                      <button
                        onClick={() => alert(`🎓 Starting ${lesson.name} lesson!`)}
                        style={{
                          padding: '8px 15px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {lesson.cost} 💎 Start
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LIBRARY LIBRARIAN TAB */}
            {locationModalTab === 'librarian' && (
              <div style={{textAlign: 'center', padding: '20px'}}>
                <div style={{fontSize: '60px', marginBottom: '20px'}}>👩‍🏫</div>
                <h3 style={{color: '#FFD700', marginBottom: '15px'}}>The Librarian</h3>
                <p style={{color: 'white', marginBottom: '20px'}}>
                  The librarian can help you with:
                </p>
                <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left'}}>
                  <div style={{color: 'white', lineHeight: '2'}}>
                    <div>✅ Finding the right books</div>
                    <div>✅ Darija pronunciation</div>
                    <div>✅ Moroccan culture questions</div>
                    <div>✅ Study room reservations</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const characterId = selectedLocation.library_data?.librarian_character_id;
                    if (characterId) {
                      const librarianChar = gameConfig.characters?.find(c => c.id === characterId);
                      if (librarianChar) {
                        setShowLocationModal(false);
                        startConversation(librarianChar);
                      } else {
                        alert('⚠️ Librarian character not found!');
                      }
                    } else {
                      alert('⚠️ Librarian conversation coming soon! Check back later.');
                    }
                  }}
                  style={{
                    padding: '15px 30px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  💬 Ask Librarian
                </button>
              </div>
            )}

            {locationModalTab === 'notes' && (
              <div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write a note about this location..."
                  style={{width: '100%', minHeight: '80px', padding: '10px', backgroundColor: '#0f3460', color: 'white', border: '2px solid #333', borderRadius: '8px', marginBottom: '10px'}}
                />
                <button
                  onClick={addLocationNote}
                  style={{padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}
                >
                  ✏️ Add Note
                </button>
                <div style={{marginTop: '20px'}}>
                  {playerNotes.filter(note => note.location_id === selectedLocation.id).map((note, idx) => (
                    <div key={idx} style={{backgroundColor: '#0f3460', padding: '12px', borderRadius: '8px', marginBottom: '10px', borderLeft: '4px solid #5C6BC0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div style={{flex: 1}}>
                        <div style={{color: 'white', marginBottom: '5px'}}>{note.note_text}</div>
                        <div style={{color: '#666', fontSize: '12px'}}>{new Date(note.timestamp).toLocaleDateString()}</div>
                      </div>
                      <button
                        onClick={async () => {
                          // Delete note from backend
                          try {
                            await axios.post(`${API_URL}/api/game/delete-note`, null, {
                              params: {
                                user_id: userId,
                                location_id: note.location_id,
                                timestamp: note.timestamp
                              }
                            });
                            // Reload notes
                            const notesRes = await axios.get(`${API_URL}/api/game/notes/${userId}`);
                            setPlayerNotes(notesRes.data.notes || []);
                          } catch (err) {
                            console.error('Error deleting note:', err);
                            alert('Failed to delete note');
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginLeft: '10px'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => { setShowLocationModal(false); setLocationModalTab('info'); setSelectedRoom(null); }}
              style={{width: '100%', marginTop: '20px', padding: '12px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer'}}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Room Confirmation Modal */}
      {showRoomConfirm && selectedRoom && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10003
        }}>


      {/* Merchant Modal - Sell Items */}
      {showMerchantModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10004
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '3px solid #FFD700'
          }}>
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{fontSize: '60px', marginBottom: '10px'}}>🧑‍💼</div>
              <h2 style={{color: '#FFD700', margin: '0 0 5px 0'}}>Merchant</h2>
              <p style={{color: '#999', margin: 0}}>Sell your collected items for gems</p>
            </div>

            {collectedItems.length === 0 ? (
              <div style={{textAlign: 'center', padding: '30px', color: '#999'}}>
                <p>You don't have any items to sell!</p>
                <p style={{fontSize: '12px'}}>Explore the map to find items.</p>
              </div>
            ) : (
              <div>
                <h4 style={{color: 'white', marginTop: 0}}>Select items to sell:</h4>
                {collectedItems.map((ci, idx) => {
                  const item = pickupItems.find(pi => pi.id === ci.item_id);
                  if (!item) return null;
                  
                  const isSelected = selectedItemsToSell.some(s => s.item_id === ci.item_id);
                  const selectedItem = selectedItemsToSell.find(s => s.item_id === ci.item_id);
                  
                  return (
                    <div key={idx} style={{
                      backgroundColor: isSelected ? '#0f5460' : '#0f3460',
                      padding: '15px',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: isSelected ? '2px solid #FFD700' : '2px solid #333'
                    }}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                          <span style={{fontSize: '32px'}}>{item.emoji}</span>
                          <div>
                            <div style={{color: 'white', fontSize: '16px', fontWeight: 'bold'}}>{item.name}</div>
                            <div style={{color: '#999', fontSize: '12px'}}>Available: {ci.quantity}</div>
                          </div>
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <div style={{color: '#FFD700', fontSize: '18px', fontWeight: 'bold'}}>
                            {item.base_value} 💎 each
                          </div>
                          <div style={{color: '#4CAF50', fontSize: '12px'}}>
                            {item.rarity}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        <input
                          type="number"
                          min="0"
                          max={ci.quantity}
                          value={selectedItem?.quantity || 0}
                          onChange={(e) => {
                            const qty = parseInt(e.target.value) || 0;
                            if (qty > 0 && qty <= ci.quantity) {
                              setSelectedItemsToSell(prev => {
                                const existing = prev.find(s => s.item_id === ci.item_id);
                                if (existing) {
                                  return prev.map(s => s.item_id === ci.item_id ? {...s, quantity: qty} : s);
                                } else {
                                  return [...prev, {item_id: ci.item_id, quantity: qty}];
                                }
                              });
                            } else if (qty === 0) {
                              setSelectedItemsToSell(prev => prev.filter(s => s.item_id !== ci.item_id));
                            }
                          }}
                          style={{
                            width: '80px',
                            padding: '8px',
                            backgroundColor: '#1a1a2e',
                            color: 'white',
                            border: '1px solid #666',
                            borderRadius: '4px'
                          }}
                        />
                        <span style={{color: 'white', fontSize: '14px'}}>
                          = {(selectedItem?.quantity || 0) * item.base_value} 💎
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div style={{
                  backgroundColor: '#0f3460',
                  padding: '20px',
                  borderRadius: '8px',
                  marginTop: '20px',
                  borderTop: '3px solid #FFD700'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: 'bold'}}>
                    <span style={{color: 'white'}}>Total:</span>
                    <span style={{color: '#FFD700'}}>
                      {selectedItemsToSell.reduce((sum, s) => {
                        const item = pickupItems.find(pi => pi.id === s.item_id);
                        return sum + (item?.base_value || 0) * s.quantity;
                      }, 0)} 💎
                    </span>
                  </div>
                </div>

                <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                  <button
                    onClick={async () => {
                      if (selectedItemsToSell.length === 0) {
                        alert('Select items to sell first!');
                        return;
                      }
                      
                      try {
                        const response = await axios.post(`${API_URL}/api/game/sell-items`, {
                          user_id: userId,
                          items: selectedItemsToSell
                        });
                        
                        if (response.data.success) {
                          alert(`✅ Sold items for ${response.data.gems_earned} gems!\nNew balance: ${response.data.new_balance} 💎`);
                          
                          // Reload inventory and game progress
                          const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
                          setCollectedItems(progressRes.data.collected_items || []);
                          setGameProgress(progressRes.data);
                          setSelectedItemsToSell([]);
                          setShowMerchantModal(false);
                        }
                      } catch (error) {
                        alert(error.response?.data?.detail || 'Failed to sell items');
                      }
                    }}
                    disabled={selectedItemsToSell.length === 0}
                    style={{
                      flex: 1,
                      padding: '15px',
                      backgroundColor: selectedItemsToSell.length > 0 ? '#4CAF50' : '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: selectedItemsToSell.length > 0 ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    💰 Sell Items
                  </button>
                  <button
                    onClick={() => {
                      setShowMerchantModal(false);
                      setSelectedItemsToSell([]);
                    }}
                    style={{
                      flex: 1,
                      padding: '15px',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trade Modal - NPC Item Trading */}
      {showTradeModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10004
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '550px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '3px solid #5C6BC0'
          }}>
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{fontSize: '60px', marginBottom: '10px'}}>🤝</div>
              <h2 style={{color: '#5C6BC0', margin: '0 0 5px 0'}}>NPC Trading</h2>
              <p style={{color: '#999', margin: 0}}>Exchange items with characters</p>
            </div>

            {tradeOffers.length === 0 ? (
              <div style={{textAlign: 'center', padding: '30px', color: '#999'}}>
                <p>No trade offers available here!</p>
                <p style={{fontSize: '12px'}}>Travel to different locations to find trade deals.</p>
              </div>
            ) : (
              <div>
                {tradeOffers.map((offer, idx) => {
                  // Check if player has required items
                  const canTrade = offer.give_items.every(gi => {
                    const playerItem = collectedItems.find(ci => ci.item_id === gi.item_id);
                    return playerItem && playerItem.quantity >= gi.quantity;
                  });

                  return (
                    <div key={idx} style={{
                      backgroundColor: '#16213e',
                      padding: '20px',
                      borderRadius: '10px',
                      marginBottom: '15px',
                      border: canTrade ? '2px solid #4CAF50' : '2px solid #444'
                    }}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                        <span style={{fontSize: '30px'}}>👤</span>
                        <div>
                          <div style={{color: '#FFD700', fontWeight: 'bold'}}>{offer.npc_name}</div>
                          <div style={{color: '#999', fontSize: '12px'}}>{offer.description}</div>
                        </div>
                      </div>

                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '15px'}}>
                        {/* Give Items */}
                        <div style={{textAlign: 'center'}}>
                          <div style={{color: '#f44336', fontSize: '12px', marginBottom: '5px'}}>You Give:</div>
                          {offer.give_items.map((gi, i) => (
                            <div key={i} style={{color: 'white', fontSize: '14px'}}>
                              {gi.emoji} {gi.name} x{gi.quantity}
                            </div>
                          ))}
                        </div>

                        <div style={{fontSize: '30px', color: '#5C6BC0'}}>➡️</div>

                        {/* Receive Items */}
                        <div style={{textAlign: 'center'}}>
                          <div style={{color: '#4CAF50', fontSize: '12px', marginBottom: '5px'}}>You Get:</div>
                          {offer.receive_items.map((ri, i) => (
                            <div key={i} style={{color: '#4CAF50', fontSize: '14px', fontWeight: 'bold'}}>
                              {ri.emoji} {ri.name} x{ri.quantity}
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API_URL}/api/game/execute-trade`, null, {
                              params: { user_id: userId, trade_id: offer.id }
                            });
                            if (response.data.success) {
                              alert(`✅ ${response.data.message}`);
                              const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
                              setCollectedItems(progressRes.data.collected_items || []);
                            }
                          } catch (error) {
                            alert(error.response?.data?.detail || 'Trade failed');
                          }
                        }}
                        disabled={!canTrade}
                        style={{
                          width: '100%',
                          marginTop: '15px',
                          padding: '12px',
                          backgroundColor: canTrade ? '#4CAF50' : '#444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: canTrade ? 'pointer' : 'not-allowed',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                      >
                        {canTrade ? '🤝 Make Trade' : '❌ Missing Items'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setShowTradeModal(false)}
              style={{
                width: '100%',
                marginTop: '10px',
                padding: '15px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delivery Modal - Order Items for Gems */}
      {showDeliveryModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10004
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '3px solid #4CAF50'
          }}>
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{fontSize: '60px', marginBottom: '10px'}}>🚚</div>
              <h2 style={{color: '#4CAF50', margin: '0 0 5px 0'}}>Order Delivery</h2>
              <p style={{color: '#999', margin: 0}}>Pay with gems, get items instantly</p>
              <p style={{color: '#FFD700', marginTop: '10px'}}>
                Your Balance: {gameProgress?.gems_balance || 0} 💎
              </p>
            </div>

            {deliveryItems.length === 0 ? (
              <div style={{textAlign: 'center', padding: '30px', color: '#999'}}>
                <p>No delivery available here!</p>
                <p style={{fontSize: '12px'}}>Try restaurants or hotels for delivery options.</p>
              </div>
            ) : (
              <div>
                {deliveryItems.map((item, idx) => {
                  const canAfford = (gameProgress?.gems_balance || 0) >= item.price_gems;

                  return (
                    <div key={idx} style={{
                      backgroundColor: '#16213e',
                      padding: '15px',
                      borderRadius: '10px',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: canAfford ? '1px solid #4CAF50' : '1px solid #444'
                    }}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <span style={{fontSize: '36px'}}>{item.emoji}</span>
                        <div>
                          <div style={{color: 'white', fontWeight: 'bold'}}>{item.name}</div>
                          <div style={{color: '#999', fontSize: '12px'}}>{item.category}</div>
                        </div>
                      </div>

                      <div style={{textAlign: 'right'}}>
                        <div style={{color: '#FFD700', fontWeight: 'bold', marginBottom: '5px'}}>
                          {item.price_gems} 💎
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const locId = currentLocation?.id || currentLocation || 'restaurant';
                              const response = await axios.post(`${API_URL}/api/game/order-delivery`, null, {
                                params: { user_id: userId, location_id: locId, item_id: item.item_id, quantity: 1 }
                              });
                              if (response.data.success) {
                                alert(`🚚 ${response.data.message}\nNew balance: ${response.data.new_balance} 💎`);
                                const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
                                setCollectedItems(progressRes.data.collected_items || []);
                                setGameProgress(progressRes.data);
                              }
                            } catch (error) {
                              alert(error.response?.data?.detail || 'Order failed');
                            }
                          }}
                          disabled={!canAfford}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: canAfford ? '#4CAF50' : '#444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: canAfford ? 'pointer' : 'not-allowed',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {canAfford ? '📦 Order' : '❌ No Gems'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setShowDeliveryModal(false)}
              style={{
                width: '100%',
                marginTop: '15px',
                padding: '15px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Road Character Modal - Character encountered during travel */}
      {showRoadCharacterModal && currentRoadCharacter && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10005
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '450px',
            width: '90%',
            border: '3px solid #FF9800',
            textAlign: 'center'
          }}>
            {/* Character Info */}
            <div style={{marginBottom: '20px'}}>
              <div style={{fontSize: '70px', marginBottom: '10px'}}>
                {gameConfig?.characters?.find(c => c.id === currentRoadCharacter.character_id)?.emoji || '👤'}
              </div>
              <h2 style={{color: '#FF9800', margin: '0 0 5px 0'}}>
                {gameConfig?.characters?.find(c => c.id === currentRoadCharacter.character_id)?.name || 'Someone'}
              </h2>
              <p style={{color: '#999', margin: 0}}>wants to talk to you!</p>
              <p style={{color: '#FFD700', marginTop: '10px', fontSize: '14px'}}>
                🚕 On the way to {selectedDestination?.name}
              </p>
            </div>

            {/* Reward Info */}
            <div style={{
              backgroundColor: '#16213e',
              padding: '15px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              <p style={{color: '#4CAF50', margin: 0, fontSize: '16px'}}>
                💎 +{currentRoadCharacter.gems_reward || 5} gems for talking
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{display: 'flex', gap: '10px'}}>
              <button
                onClick={async () => {
                  // Award gems for talking
                  const gemsEarned = currentRoadCharacter.gems_reward || 5;
                  setGameProgress(prev => ({
                    ...prev,
                    gems_balance: (prev.gems_balance || 0) + gemsEarned
                  }));
                  
                  setRecentTransactions(prev => [
                    { 
                      item: `Talked to ${gameConfig?.characters?.find(c => c.id === currentRoadCharacter.character_id)?.name || 'character'}`,
                      amount: gemsEarned, 
                      timestamp: new Date() 
                    },
                    ...prev
                  ]);
                  
                  // TODO: Open actual conversation if conversation_id exists
                  alert(`✅ You earned ${gemsEarned} gems!`);
                  
                  setShowRoadCharacterModal(false);
                  setCurrentRoadCharacter(null);
                  
                  // REMOVED: continueToNextStop() - now using continuous animation
                  // Taxi continues automatically via System B animation loop
                }}
                style={{
                  flex: 1,
                  padding: '15px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                🗣️ Speak
              </button>
              
              <button
                onClick={() => {
                  setShowRoadCharacterModal(false);
                  setCurrentRoadCharacter(null);
                  
                  // REMOVED: continueToNextStop() - now using continuous animation
                  // Taxi continues automatically via System B animation loop
                }}
                style={{
                  flex: 1,
                  padding: '15px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                🚕 Keep Driving
              </button>
            </div>
          </div>
        </div>
      )}

          <div style={{backgroundColor: '#1a1a2e', padding: '40px', borderRadius: '15px', maxWidth: '500px', width: '90%', textAlign: 'center', border: '3px solid #4CAF50'}}>
            <div style={{fontSize: '80px', marginBottom: '20px'}}>🎉</div>
            <h2 style={{color: '#4CAF50'}}>Room Booked!</h2>
            <h3 style={{color: '#FFD700'}}>Room #{selectedRoom.room_number}</h3>
            <div style={{backgroundColor: '#0f3460', padding: '20px', borderRadius: '10px', marginBottom: '20px', textAlign: 'left'}}>
              <h4 style={{color: '#FFD700'}}>🏨 Room Amenities:</h4>
              <div style={{color: 'white', lineHeight: '2'}}>
                <div>📺 TV / Talfaza (تلفزة)</div>
                <div>🚿 Shower / Douch (دوش)</div>
                <div>🛏️ Bed / Frach (فراش)</div>
                <div>🪟 Window / Chrajm (شرجم)</div>
              </div>
              <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#1a1a2e', borderRadius: '5px'}}>
                <p style={{color: '#4CAF50', margin: 0}}>💚 Energy restored to 100%!</p>
              </div>
            </div>
            <button
              onClick={() => { setShowRoomConfirm(false); setSelectedRoom(null); }}
              style={{padding: '15px 30px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px'}}
            >
              ✅ Exit
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

// Locations Panel Component - Uses Road Network System
function LocationsPanel({ locations, currentLocation, previousLocation, selectedDestination, roads, onLocationClick, theme, isTaxiAnimating, setShowTaxiConversation }) {
  
  // Check if there's a road connecting current location to target location
  const hasRoadTo = (targetLocId) => {
    if (!currentLocation || !roads || roads.length === 0) return false;
    const currentId = currentLocation.id || currentLocation;
    
    return roads.some(road => {
      const connects = road.connects || [];
      return (connects.includes(currentId) && connects.includes(targetLocId));
    });
  };
  
  // Check if location is reachable (directly or via other locations)
  const isReachable = (targetLocId) => {
    if (!roads || roads.length === 0) return false;
    // For now, check direct connection. Could expand to multi-hop
    return hasRoadTo(targetLocId);
  };
  
  // Get roads from current location
  const getRoadsFromCurrent = () => {
    if (!currentLocation || !roads) return [];
    const currentId = currentLocation.id || currentLocation;
    return roads.filter(road => road.connects?.includes(currentId));
  };
  
  // Check if location has any roads (can be a starting point)
  const hasAnyRoads = (locId) => {
    if (!roads) return false;
    return roads.some(road => road.connects?.includes(locId));
  };

  return (
    <div style={styles.panelSection}>
      <h3 style={{...styles.panelTitle, color: theme.text}}>🗺️ Travel Destinations</h3>
      
      {/* Current Location Banner */}
      {currentLocation && (
        <div style={{
          backgroundColor: '#1a4d7a',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '2px solid #5C6BC0'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            <span style={{fontSize: '24px'}}>📍</span>
            <div>
              <div style={{color: 'white', fontWeight: 'bold'}}>
                You are at: {currentLocation.bubble_text || currentLocation.name}
              </div>
              <div style={{color: '#90CAF9', fontSize: '12px'}}>
                {getRoadsFromCurrent().length} road{getRoadsFromCurrent().length !== 1 ? 's' : ''} available
              </div>
            </div>
          </div>
        </div>
      )}
      
      {locations.length === 0 ? (
        <p style={styles.emptyMessage}>No locations available yet</p>
      ) : (
        <div style={styles.locationList}>
          {locations.map(loc => {
            const isCurrentLocation = currentLocation && (loc.id === currentLocation.id);
            const isDestination = isTaxiAnimating && selectedDestination && (loc.id === selectedDestination.id);
            const canTravelTo = !isCurrentLocation && (currentLocation ? hasRoadTo(loc.id) : hasAnyRoads(loc.id));
            const hasRoads = hasAnyRoads(loc.id);
            
            // Determine status
            let statusMessage = '';
            let statusColor = '#666';
            
            if (isCurrentLocation) {
              statusMessage = '✅ You are here';
              statusColor = '#2196F3';
            } else if (isDestination) {
              statusMessage = '🚕 Traveling here...';
              statusColor = '#E91E63';
            } else if (!currentLocation && hasRoads) {
              statusMessage = '🚕 Can start here';
              statusColor = '#4CAF50';
            } else if (!currentLocation && !hasRoads) {
              statusMessage = '🚫 No roads here';
              statusColor = '#F44336';
            } else if (canTravelTo) {
              statusMessage = '🛣️ Road available';
              statusColor = '#4CAF50';
            } else {
              statusMessage = '⚠️ No direct road';
              statusColor = '#FF9800';
            }
            
            return (
              <div
                key={loc.id}
                style={{
                  backgroundColor: isCurrentLocation ? '#1a4d7a' : (isDestination ? '#4a1a4a' : theme.cardBg),
                  padding: '15px',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  border: isCurrentLocation ? '3px solid #5C6BC0' : (isDestination ? '3px solid #E91E63' : `2px solid ${theme.panelBorder}`),
                  opacity: (!currentLocation && !hasRoads) ? 0.5 : 1
                }}
              >
                <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                  <span style={{fontSize: '28px', marginRight: '12px'}}>
                    {isCurrentLocation ? '📍' : (isDestination ? '🎯' : (loc.location_type === 'hotel' ? '🏨' : loc.location_type === 'restaurant' ? '🍽️' : loc.location_type === 'gym' ? '🏋️' : '🗺️'))}
                  </span>
                  <div style={{flex: 1}}>
                    <strong style={{fontSize: '16px', color: theme.text}}>
                      {loc.bubble_text || loc.name}
                    </strong>
                    <div style={{fontSize: '13px', color: statusColor, fontWeight: 'bold', marginTop: '4px'}}>
                      {statusMessage}
                    </div>
                  </div>
                </div>
                
                {/* Buttons */}
                {isCurrentLocation ? (
                  // At current location - Show speak and info
                  <div style={{display: 'flex', gap: '6px'}}>
                    <button
                      onClick={() => onLocationClick(loc, 'speak')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      💬 Explore
                    </button>
                    <button
                      onClick={() => onLocationClick(loc, 'info')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      📍 Info
                    </button>
                  </div>
                ) : canTravelTo ? (
                  // Can travel - Show call taxi
                  <div style={{display: 'flex', gap: '6px'}}>
                    <button
                      onClick={() => onLocationClick(loc, 'taxi')}
                      disabled={isTaxiAnimating}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        backgroundColor: isTaxiAnimating ? (isDestination ? '#E91E63' : '#666') : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isTaxiAnimating ? 'not-allowed' : 'pointer'
                      }}
                    >
                      🚕 {isTaxiAnimating ? (isDestination ? 'On the way!' : 'Busy') : 'Call Taxi'}
                    </button>
                    <button
                      onClick={() => onLocationClick(loc, 'info')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      📍 Info
                    </button>
                  </div>
                ) : !currentLocation && hasRoads ? (
                  // No current location but has roads - Can start here
                  <div style={{display: 'flex', gap: '6px'}}>
                    <button
                      onClick={() => onLocationClick(loc, 'start')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      🎯 Start Here
                    </button>
                    <button
                      onClick={() => onLocationClick(loc, 'info')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      📍 Info
                    </button>
                  </div>
                ) : (
                  // No road available
                  <button
                    onClick={() => onLocationClick(loc, 'info')}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    📍 View Info
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Rewards Panel Component
function RewardsPanel({ 
  dailyLoginClaimed, 
  claimDailyReward, 
  rankInfo, 
  balance, 
  gemFeatures,
  spendingLocked,
  setSpendingLocked,
  savingsGoal,
  smartSuggestion,
  theme
}) {
  return (
    <div style={styles.panelSection}>
      <h3 style={{...styles.panelTitle, color: theme.text}}>🎁 Rewards & Progress</h3>
      
      {/* Daily Login */}
      {gemFeatures.dailyLoginRewards && (
        <div style={{...styles.rewardCard, backgroundColor: theme.cardBg, color: theme.text}}>
          <h4>🎁 Daily Login Bonus</h4>
          {dailyLoginClaimed ? (
            <p style={{color: '#4CAF50'}}>✅ Already claimed today!</p>
          ) : (
            <button onClick={claimDailyReward} style={styles.claimBtn}>
              Claim 50 Gems
            </button>
          )}
        </div>
      )}

      {/* Ranked Mode */}
      {gemFeatures.rankedMode && (
        <div style={{...styles.rewardCard, backgroundColor: theme.cardBg, color: theme.text}}>
          <h4>🏆 Rank: {rankInfo.rank}</h4>
          <p>Current Balance: {balance} gems</p>
          {rankInfo.nextRank ? (
            <p>Next Rank: {rankInfo.nextRank} gems</p>
          ) : (
            <p style={{color: '#FFD700'}}>⭐ You've reached the highest rank!</p>
          )}
        </div>
      )}

      {/* Smart Suggestions */}
      {gemFeatures.smartSuggestions && smartSuggestion && (
        <div style={{...styles.rewardCard, backgroundColor: '#E3F2FD'}}>
          <p>{smartSuggestion}</p>
        </div>
      )}

      {/* Savings Goals */}
      {gemFeatures.savingsGoals && (
        <div style={{...styles.rewardCard, backgroundColor: theme.cardBg, color: theme.text}}>
          <h4>🎯 Savings Goal</h4>
          <p>Target: {savingsGoal.target} gems</p>
          <p>Current: {balance} gems</p>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${(balance / savingsGoal.target) * 100}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Spending Lock */}
      {gemFeatures.spendingLock && (
        <div style={{...styles.rewardCard, backgroundColor: theme.cardBg, color: theme.text}}>
          <h4>🔒 Spending Lock</h4>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={spendingLocked}
              onChange={(e) => setSpendingLocked(e.target.checked)}
            />
            Lock spending to prevent accidental use
          </label>
        </div>
      )}
    </div>
  );
}

// ============================================
// INVENTORY PANEL - Shows collected items
// ============================================
function InventoryPanel({ collectedItems, pickupItems, userId, setCollectedItems, setGameProgress, theme }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const handleEatItem = async (item) => {
    try {
      const response = await axios.post(`${API_URL}/api/game/eat-item`, null, {
        params: { user_id: userId, item_id: item.id }
      });
      if (response.data.success) {
        alert(`✅ Ate ${item.name}! Energy +${response.data.energy_restored}%`);
        // Reload inventory
        const progressRes = await axios.get(`${API_URL}/api/game/progress/${userId}`);
        setCollectedItems(progressRes.data.collected_items || []);
        setGameProgress(progressRes.data);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to eat item');
    }
  };

  const totalItems = collectedItems.reduce((sum, ci) => sum + ci.quantity, 0);
  const foodItems = collectedItems.filter(ci => {
    const item = pickupItems.find(pi => pi.id === ci.item_id);
    return item && item.category === 'food';
  });
  const natureItems = collectedItems.filter(ci => {
    const item = pickupItems.find(pi => pi.id === ci.item_id);
    return item && item.category === 'nature';
  });

  return (
    <div style={styles.panelSection}>
      <h3 style={{...styles.panelTitle, color: theme.text}}>🎒 Inventory</h3>
      
      {/* Inventory Stats */}
      <div style={{
        backgroundColor: '#16213e',
        padding: '12px',
        borderRadius: '8px',
        marginBottom: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{color: 'white', fontSize: '14px'}}>
          📦 Items: <strong>{totalItems}/30</strong>
        </div>
        <div style={{
          width: '100px',
          height: '8px',
          backgroundColor: '#333',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${(totalItems / 30) * 100}%`,
            height: '100%',
            backgroundColor: totalItems > 25 ? '#f44336' : totalItems > 15 ? '#FF9800' : '#4CAF50',
            transition: 'width 0.3s'
          }} />
        </div>
      </div>

      {collectedItems.length === 0 ? (
        <div style={{textAlign: 'center', padding: '40px 20px', color: '#666'}}>
          <div style={{fontSize: '48px', marginBottom: '15px'}}>🎒</div>
          <p style={{margin: 0, color: theme.text}}>No items yet</p>
          <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#999'}}>
            Visit locations to find items!
          </p>
        </div>
      ) : (
        <>
          {/* Food Items */}
          {foodItems.length > 0 && (
            <div style={{marginBottom: '20px'}}>
              <h4 style={{color: '#FFD700', margin: '0 0 10px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                <span>🍎</span> Food Items
                <span style={{color: '#999', fontWeight: 'normal'}}>({foodItems.length})</span>
              </h4>
              {foodItems.map((ci, idx) => {
                const item = pickupItems.find(pi => pi.id === ci.item_id);
                if (!item) return null;
                return (
                  <div key={idx} style={{
                    backgroundColor: '#16213e',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: item.rarity === 'rare' ? '1px solid #FFD700' : '1px solid #0f3460'
                  }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                      <span style={{fontSize: '28px'}}>{item.emoji}</span>
                      <div>
                        <div style={{color: 'white', fontWeight: 'bold', fontSize: '14px'}}>{item.name}</div>
                        <div style={{color: '#999', fontSize: '11px'}}>
                          x{ci.quantity} • {item.rarity} • +{item.energy_restore}% energy
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleEatItem(item)}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      🍴 Eat
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Nature Items */}
          {natureItems.length > 0 && (
            <div>
              <h4 style={{color: '#4CAF50', margin: '0 0 10px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                <span>🌿</span> Nature Items
                <span style={{color: '#999', fontWeight: 'normal'}}>({natureItems.length})</span>
              </h4>
              {natureItems.map((ci, idx) => {
                const item = pickupItems.find(pi => pi.id === ci.item_id);
                if (!item) return null;
                return (
                  <div key={idx} style={{
                    backgroundColor: '#16213e',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: item.rarity === 'rare' ? '1px solid #FFD700' : '1px solid #0f3460'
                  }}>
                    <span style={{fontSize: '28px'}}>{item.emoji}</span>
                    <div>
                      <div style={{color: 'white', fontWeight: 'bold', fontSize: '14px'}}>{item.name}</div>
                      <div style={{color: '#999', fontSize: '11px'}}>
                        x{ci.quantity} • {item.rarity} • Worth {item.base_value} gems
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================
// POUCH & TRADE PANEL - Balance, Sell, Trade, Order
// ============================================
function PouchTradePanel({ 
  balance, transactions, lowBalanceWarning, theme, 
  collectedItems, pickupItems, 
  setShowMerchantModal, setShowTradeModal, setShowDeliveryModal,
  userId, setCollectedItems, setGameProgress, currentLocation 
}) {
  const [pouchTab, setPouchTab] = React.useState('balance');

  return (
    <div style={styles.panelSection}>
      <h3 style={{...styles.panelTitle, color: theme.text}}>💰 Pouch & Trade</h3>
      
      {/* Sub-Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #0f3460',
        marginBottom: '15px',
        gap: '2px'
      }}>
        {['balance', 'sell', 'trade', 'order'].map(tab => (
          <button
            key={tab}
            onClick={() => setPouchTab(tab)}
            style={{
              flex: 1,
              padding: '8px 4px',
              backgroundColor: pouchTab === tab ? '#16213e' : 'transparent',
              color: pouchTab === tab ? theme.text : '#666',
              border: 'none',
              borderBottom: pouchTab === tab ? '2px solid #5C6BC0' : 'none',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: pouchTab === tab ? 'bold' : 'normal'
            }}
          >
            {tab === 'balance' && '💎'}
            {tab === 'sell' && '🛒'}
            {tab === 'trade' && '🔄'}
            {tab === 'order' && '🚚'}
            <br/>{tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Balance Tab */}
      {pouchTab === 'balance' && (
        <>
          <div style={{
            backgroundColor: '#16213e',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center',
            marginBottom: '15px'
          }}>
            <div style={{fontSize: '36px', color: '#FFD700', fontWeight: 'bold'}}>
              {balance} 💎
            </div>
            <div style={{color: '#999', fontSize: '12px', marginTop: '5px'}}>
              Current Balance
            </div>
          </div>
          
          {lowBalanceWarning && (
            <div style={{
              backgroundColor: '#FFF3E0',
              color: '#E65100',
              padding: '10px',
              borderRadius: '6px',
              marginBottom: '15px',
              fontSize: '12px'
            }}>
              ⚠️ Low balance! Sell items or complete quests.
            </div>
          )}
          
          <h4 style={{color: theme.text, margin: '15px 0 10px 0', fontSize: '13px'}}>📜 Recent Transactions</h4>
          {transactions.length === 0 ? (
            <p style={{color: '#666', fontSize: '12px', textAlign: 'center'}}>No transactions yet</p>
          ) : (
            <div style={{maxHeight: '200px', overflow: 'auto'}}>
              {transactions.slice(0, 8).map((tx, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#16213e',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '12px'
                }}>
                  <span style={{color: theme.text}}>{tx.description || tx.item}</span>
                  <span style={{color: tx.amount < 0 ? '#f44336' : '#4CAF50', fontWeight: 'bold'}}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount} 💎
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Sell Tab */}
      {pouchTab === 'sell' && (
        <div style={{textAlign: 'center', padding: '20px'}}>
          <div style={{fontSize: '48px', marginBottom: '15px'}}>🛒</div>
          <h4 style={{color: theme.text, margin: '0 0 10px 0'}}>Sell to Merchant</h4>
          <p style={{color: '#999', fontSize: '12px', marginBottom: '20px'}}>
            Exchange your items for gems
          </p>
          <button
            onClick={() => setShowMerchantModal(true)}
            disabled={collectedItems.length === 0}
            style={{
              padding: '12px 30px',
              backgroundColor: collectedItems.length === 0 ? '#444' : '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: collectedItems.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            💰 Open Merchant
          </button>
          {collectedItems.length === 0 && (
            <p style={{color: '#999', fontSize: '11px', marginTop: '10px'}}>
              Collect items first to sell
            </p>
          )}
        </div>
      )}

      {/* Trade Tab */}
      {pouchTab === 'trade' && (
        <div style={{textAlign: 'center', padding: '20px'}}>
          <div style={{fontSize: '48px', marginBottom: '15px'}}>🔄</div>
          <h4 style={{color: theme.text, margin: '0 0 10px 0'}}>NPC Trading</h4>
          <p style={{color: '#999', fontSize: '12px', marginBottom: '20px'}}>
            Trade items with characters at locations
          </p>
          <button
            onClick={() => setShowTradeModal(true)}
            style={{
              padding: '12px 30px',
              backgroundColor: '#5C6BC0',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            🤝 View Trades
          </button>
          <p style={{color: '#666', fontSize: '11px', marginTop: '10px'}}>
            Travel to locations to discover trade offers
          </p>
        </div>
      )}

      {/* Order/Delivery Tab */}
      {pouchTab === 'order' && (
        <div style={{textAlign: 'center', padding: '20px'}}>
          <div style={{fontSize: '48px', marginBottom: '15px'}}>🚚</div>
          <h4 style={{color: theme.text, margin: '0 0 10px 0'}}>Order Delivery</h4>
          <p style={{color: '#999', fontSize: '12px', marginBottom: '20px'}}>
            Order items and pay with gems
          </p>
          <button
            onClick={() => setShowDeliveryModal(true)}
            style={{
              padding: '12px 30px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            📦 Order Now
          </button>
          <p style={{color: '#666', fontSize: '11px', marginTop: '10px'}}>
            Items delivered instantly to your inventory
          </p>
        </div>
      )}
    </div>
  );
}

// Pouch Panel Component (Legacy - keeping for compatibility)
function PouchPanel({ balance, transactions, lowBalanceWarning, theme, collectedItems, pickupItems, setShowMerchantModal, userId, setCollectedItems, setGameProgress }) {
  const [pouchTab, setPouchTab] = React.useState('balance');

  return (
    <div style={styles.panelSection}>
      <h3 style={{...styles.panelTitle, color: theme.text}}>💰 Pouch</h3>
      
      {/* Pouch Sub-Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #0f3460',
        marginBottom: '15px'
      }}>
        <button
          onClick={() => setPouchTab('balance')}
          style={{
            flex: 1,
            padding: '10px 8px',
            backgroundColor: pouchTab === 'balance' ? theme.cardBg : 'transparent',
            color: pouchTab === 'balance' ? theme.text : '#999',
            border: 'none',
            borderBottom: pouchTab === 'balance' ? `3px solid #5C6BC0` : 'none',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: pouchTab === 'balance' ? 'bold' : 'normal',
            transition: 'all 0.2s'
          }}
        >
          💎 Balance
        </button>
        <button
          onClick={() => setPouchTab('assets')}
          style={{
            flex: 1,
            padding: '10px 8px',
            backgroundColor: pouchTab === 'assets' ? theme.cardBg : 'transparent',
            color: pouchTab === 'assets' ? theme.text : '#999',
            border: 'none',
            borderBottom: pouchTab === 'assets' ? `3px solid #5C6BC0` : 'none',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: pouchTab === 'assets' ? 'bold' : 'normal',
            transition: 'all 0.2s'
          }}
        >
          📦 Assets
        </button>
      </div>

      {/* Balance Tab Content */}
      {pouchTab === 'balance' && (
        <>
          <div style={{...styles.balanceCard, backgroundColor: theme.cardBg, color: theme.text}}>
            <h2 style={{margin: '0 0 10px 0', color: '#5C6BC0'}}>
              {balance} 💎
            </h2>
            <p style={{margin: 0, color: '#666'}}>Current Balance</p>
          </div>

          {lowBalanceWarning && (
            <div style={{...styles.warningCard, backgroundColor: '#FFF3E0'}}>
              ⚠️ Low balance! Complete quests to earn more gems.
            </div>
          )}

          <h4 style={{marginTop: '20px'}}>📜 Transaction History</h4>
          
          {transactions.length === 0 ? (
            <p style={styles.emptyMessage}>No transactions yet</p>
          ) : (
            <div style={styles.transactionList}>
              {transactions.slice(0, 10).map((tx, idx) => (
                <div key={idx} style={{...styles.transactionItem, backgroundColor: theme.cardBg, color: theme.text}}>
                  <span>{tx.item}</span>
                  <span style={{
                    color: tx.amount < 0 ? '#f44336' : '#4CAF50',
                    fontWeight: 'bold'
                  }}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount} 💎
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Assets Tab Content - INVENTORY SYSTEM */}
      {pouchTab === 'assets' && (
        <div style={{padding: '10px', color: theme.text}}>
          <h4 style={{margin: '0 0 15px 0', color: theme.text, textAlign: 'center'}}>📦 Inventory</h4>
          
          {/* Inventory Stats */}
          <div style={{
            backgroundColor: '#0f3460',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <div style={{color: 'white', fontSize: '14px'}}>
              Items: {collectedItems.reduce((sum, item) => sum + item.quantity, 0)}/30
            </div>
            <div style={{color: '#FFD700', fontSize: '14px'}}>
              💰 <button
                onClick={() => setShowMerchantModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FFD700',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Sell to Merchant
              </button>
            </div>
          </div>

          {/* Inventory Items */}
          {collectedItems.length === 0 ? (
            <div style={{textAlign: 'center', padding: '30px', color: '#999'}}>
              <div style={{fontSize: '48px', marginBottom: '10px'}}>🎒</div>
              <p>No items collected yet</p>
              <p style={{fontSize: '12px'}}>Explore the map to find items!</p>
            </div>
          ) : (
            <div>
              {/* Food Category */}
              {collectedItems.filter(ci => {
                const item = pickupItems.find(pi => pi.id === ci.item_id);
                return item && item.category === 'food';
              }).length > 0 && (
                <div style={{marginBottom: '15px'}}>
                  <h5 style={{color: '#FFD700', margin: '0 0 8px 0', fontSize: '13px'}}>🍎 Food Items</h5>
                  {collectedItems.filter(ci => {
                    const item = pickupItems.find(pi => pi.id === ci.item_id);
                    return item && item.category === 'food';
                  }).map((ci, idx) => {
                    const item = pickupItems.find(pi => pi.id === ci.item_id);
                    return (
                      <div key={idx} style={{
                        backgroundColor: '#0f3460',
                        padding: '10px',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', flex: 1}}>
                          <span style={{fontSize: '24px'}}>{item?.emoji || '📦'}</span>
                          <div>
                            <div style={{color: 'white', fontSize: '14px'}}>{item?.name || 'Unknown'}</div>
                            <div style={{color: '#999', fontSize: '11px'}}>x{ci.quantity}</div>
                          </div>
                        </div>
                        {item?.can_eat && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API_URL}/api/game/eat-item`, null, {
                                  params: { user_id: userId, item_id: item.id }
                                });
                                // Reload inventory
                                const updatedProgress = await axios.get(`${API_URL}/api/game/progress/${userId}`);
                                setCollectedItems(updatedProgress.data.collected_items || []);
                                setGameProgress(updatedProgress.data);
                                alert(`✅ Ate ${item.name}! Energy restored +${item.energy_restore}%`);
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Failed to eat item');
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            🍴 Eat
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Nature Category */}
              {collectedItems.filter(ci => {
                const item = pickupItems.find(pi => pi.id === ci.item_id);
                return item && item.category === 'nature';
              }).length > 0 && (
                <div>
                  <h5 style={{color: '#4CAF50', margin: '0 0 8px 0', fontSize: '13px'}}>🌿 Nature Items</h5>
                  {collectedItems.filter(ci => {
                    const item = pickupItems.find(pi => pi.id === ci.item_id);
                    return item && item.category === 'nature';
                  }).map((ci, idx) => {
                    const item = pickupItems.find(pi => pi.id === ci.item_id);
                    return (
                      <div key={idx} style={{
                        backgroundColor: '#0f3460',
                        padding: '10px',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span style={{fontSize: '24px'}}>{item?.emoji || '📦'}</span>
                        <div>
                          <div style={{color: 'white', fontSize: '14px'}}>{item?.name || 'Unknown'}</div>
                          <div style={{color: '#999', fontSize: '11px'}}>x{ci.quantity}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// RewardsPanel Component (keeping for future use)

// Styles
const styles = {
  container: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    color: 'white'
  },
  statsBar: {
    display: 'flex',
    gap: '20px',
    padding: '15px 20px',
    backgroundColor: '#16213e',
    borderBottom: '2px solid #0f3460',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'white',
    fontSize: '14px'
  },
  statIcon: {
    fontSize: '20px'
  },
  energyBar: {
    width: '80px',
    height: '8px',
    backgroundColor: '#333',
    borderRadius: '4px',
    overflow: 'hidden',
    marginLeft: '8px'
  },
  energyFill: {
    height: '100%',
    backgroundColor: '#4CAF50',
    transition: 'width 0.3s'
  },
  genderBtn: {
    padding: '8px 16px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s'
  },
  saveBtn: {
    marginLeft: 'auto',
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  gameArea: {
    display: 'flex',
    flex: 1,
    position: 'relative'
  },
  canvasContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    position: 'relative'
  },
  canvas: {
    border: '3px solid #0f3460',
    borderRadius: '8px',
    cursor: 'grab',
    backgroundColor: '#000'
  },
  zoomControls: {
    position: 'absolute',
    top: '30px',
    right: '30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: '10px',
    borderRadius: '8px'
  },
  zoomBtn: {
    width: '40px',
    height: '40px',
    backgroundColor: '#5C6BC0',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold'
  },
  zoomLabel: {
    color: 'white',
    textAlign: 'center',
    fontSize: '12px',
    marginTop: '5px'
  },
  rightPanel: {
    backgroundColor: '#16213e',
    borderLeft: '2px solid #0f3460',
    overflow: 'hidden',
    transition: 'width 0.3s',
    display: 'flex',
    flexDirection: 'column'
  },
  panelTabs: {
    display: 'flex',
    borderBottom: '2px solid #0f3460',
    backgroundColor: '#0f3460'
  },
  panelTab: {
    flex: 1,
    padding: '12px 8px',
    backgroundColor: 'transparent',
    color: '#aaa',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  panelTabActive: {
    backgroundColor: '#16213e',
    color: 'white',
    borderBottom: '3px solid #5C6BC0'
  },
  panelContent: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  panelSection: {
    color: 'white'
  },
  panelTitle: {
    margin: '0 0 15px 0',
    fontSize: '18px',
    color: '#5C6BC0'
  },
  locationList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '15px'
  },
  locationBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '15px',
    backgroundColor: '#0f3460',
    border: '2px solid transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    color: 'white',
    textAlign: 'left',
    transition: 'all 0.2s'
  },
  locationBtnActive: {
    borderColor: '#5C6BC0',
    backgroundColor: '#1a4d7a'
  },
  locationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  hint: {
    fontSize: '12px',
    color: '#888',
    marginTop: '10px'
  },
  rewardCard: {
    backgroundColor: '#0f3460',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px',
    border: '1px solid #1a4d7a'
  },
  claimBtn: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  progressBar: {
    width: '100%',
    height: '10px',
    backgroundColor: '#333',
    borderRadius: '5px',
    overflow: 'hidden',
    marginTop: '10px'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#5C6BC0',
    transition: 'width 0.3s'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer'
  },
  balanceCard: {
    backgroundColor: '#0f3460',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '2px solid #5C6BC0'
  },
  warningCard: {
    padding: '12px',
    borderRadius: '6px',
    marginTop: '15px',
    color: '#333'
  },
  transactionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '10px'
  },
  transactionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px',
    backgroundColor: '#0f3460',
    borderRadius: '6px',
    fontSize: '13px'
  },
  emptyMessage: {
    color: '#888',
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px'
  },
  togglePanelBtn: {
    position: 'absolute',
    right: '350px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '20px 8px',
    backgroundColor: '#16213e',
    color: 'white',
    border: '2px solid #0f3460',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    zIndex: 10
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000
  },
  conversationModal: {
    background: '#1a1a2e',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '2px solid #667eea',
    display: 'flex',
    flexDirection: 'column'
  },
  conversationHeader: {
    padding: '20px 25px',
    borderBottom: '2px solid #667eea',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
  },
  conversationContent: {
    padding: '25px',
    overflowY: 'auto',
    flex: 1
  }
};

export default Game;
